import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  Fragment
} from 'react'
import { floatingButtonStyle } from 'components/atoms/FloatingButton'
import { useFloatingSlot, FLOATING_PRIORITY } from 'components/atoms/floatingStack'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import {
  CircularProgress,
  Menu,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Chip,
  Typography,
  Skeleton
} from '@mui/material'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import AddIcon from '@mui/icons-material/Add'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import GridViewIcon from '@mui/icons-material/GridView'
import ListIcon from '@mui/icons-material/List'
import FolderIcon from '@mui/icons-material/Folder'
import UploadIcon from '@mui/icons-material/Upload'
import SortIcon from '@mui/icons-material/Sort'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import SecurityIcon from '@mui/icons-material/Security'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import CloseIcon from '@mui/icons-material/Close'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { FixedSizeGrid, FixedSizeList } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import { useDrag, useDrop, useDragLayer } from 'react-dnd'
import { useDropzone } from 'react-dropzone'

import { getSelectedBp, localSearchFileResults, localSearchTruncated, selectIsSearchLoading } from 'features/bpDetails/ducks/selectors'
import { searchLocalFilesRoutine } from 'features/bpDetails/ducks/actions'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { getFileTypes, selectBpFilesList, selectCustomFolderFiles } from 'ducks/files/selectors'
import {
  selectFileCategories,
  selectFolders
} from 'ducks/dictionaries/selectors'
import { loadCustomFolderFilesRoutine, invalidateCustomFolderCacheRoutine } from 'ducks/files/actions'
import FileLoadingProgress from 'features/bpDetails/Components/files/FileLoadingProgress'
import { canEditBp } from 'utils/user'
import { getFileTypeName } from 'utils/bpData'
import { pathOr } from 'ramda'
import CustomViewFolders from './CustomViewFolders'
import SharePointIntegrationModal from './SharePoint/SharePointIntegrationModal'
import SharePointAccessControlModal from './SharePoint/SharePointAccessControlModal'
import SharePointReauthModal from './SharePoint/SharePointReauthModal'
import File, { FilePermissionsModal } from './File'
import ReclassifyFileModal from './ReclassifyFileModal'
import ViewedStatusModal from './files/ViewedStatusModal'
import APIService from 'services/APIService'
import CookieService from 'services/CookieService'
import {
  getAutoScrollSpeed,
  getIntersectingItemIds,
  getSelectionRange,
  haveSameSelection,
  hasExceededDragThreshold,
  isTextEditingTarget,
  mergeUniqueSelection
} from 'utils/fileMultiSelection'
import BulkShareFilesModal from '../../../features/bpDetails/Components/ShareBulkFilesModal'
import BulkEditFileModal from '../../../features/bpDetails/Components/EditBulkFilesModal'
import BulkUnshareFilesModal from '../../../features/bpDetails/Components/UnshareBulkFilesModal'

import EditFileModal from '../../../features/bpDetails/Components/EditFileModal'
import EditIcon from '@mui/icons-material/Edit'
import ShareIcon from '@mui/icons-material/Share'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import { Document as PDFDocument, Page, pdfjs } from 'react-pdf'

// Grid layout constants (reduced width to fit 4 items per row)
const GRID_ITEM_WIDTH = 240
const MOBILE_SCROLL_GUTTER = 12
const GRID_ITEM_HEIGHT = 470

// DraggableFile Component
const DraggableFile = ({ file, children, selectedFiles, canEdit, viewMode }) => {
  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: 'file',
    item: () => {
      // If this file is part of a multi-selection, drag all selected files
      const filesToDrag =
        selectedFiles.length > 0 && selectedFiles.some(f => f.id === file.id)
          ? selectedFiles
          : [file]

      return {
        files: filesToDrag, // Pass multiple files
        file // Keep for backward compatibility
      }
    },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    }),
    end: (item, monitor) => {
      // Optional: handle drag end
    }
  })

  // Use empty image as drag preview to hide the default browser preview
  React.useEffect(() => {
    const img = new Image()
    img.src =
      'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs='
    dragPreview(img, { anchorX: 0, anchorY: 0 })
  }, [dragPreview])

  // Check if this file is selected for visual feedback
  const isSelected = selectedFiles.some(sf => sf.id === file.id)

  return (
    <div
      style={{
        opacity: isDragging ? 0.3 : 1,
        cursor: 'default',
        transition: 'opacity 0.2s ease',
        borderRadius: isSelected ? '8px' : '0',
        position: 'relative'
      }}
    >
      {canEdit && (
        <FileDragHandle
          ref={drag}
          role='button'
          aria-label='Drag document to a folder'
          title='Drag document to a folder'
          $listView={viewMode === 'list'}
          onClick={event => event.stopPropagation()}
        >
          <DragIndicatorIcon />
        </FileDragHandle>
      )}
      {children}
    </div>
  )
}

// FileGridItem Component for virtualized grid
const FileGridItem = ({ columnIndex, rowIndex, style, data }) => {
  const {
    items,
    columnCount,
    selectedFiles,
    toggleFileSelection,
    isDragSelecting,
    getFileTypeName,
    fileTypes,
    searchResults,
    canEdit,
    getCategoryColor,
    handleOpenReclassifyModal,
    getFilePathInCustomView,
    isMobile,
    handleEditModalOpen,
    setPermissionsFile,
    setViewedStatusFile,
    searchQuery,
    searchMode,
    isTouchDevice
  } = data
  const index = rowIndex * columnCount + columnIndex

  if (!items || index >= items.length) {
    return null
  }

  const file = items[index]
  const gap = isMobile ? 6 : 8
  const adjustedStyle = {
    ...style,
    left: style.left + gap,
    top: style.top + gap,
    width: style.width - (gap * 2),
    height: style.height - (gap * 2),
    position: 'absolute',
    minWidth: 0
  }

  return (
    <div
      key={`grid-${file.id}`}
      style={adjustedStyle}
      className='file-card'
      data-file-id={file.id}
    >
      <GridItemContainer>
        <DraggableFile file={file} selectedFiles={selectedFiles} canEdit={canEdit} viewMode='grid'>
          <File
            file={file}
            type={getFileTypeName(
              pathOr('', ['fileType', 'id'], file),
              fileTypes,
              file.isPrivate
            )}
            viewType='grid'
            hideBulkSelect={false}
            viewMode='grid'
            showThumbnail={true}
            isSharedWithUnits={file.isSharedWithUnits}
            customPath={getFilePathInCustomView(file)}
            isInSearchBar={!!searchResults}
            canEdit={canEdit}
            categoryColor={getCategoryColor(file)}
            onOpenReclassifyModal={() => handleOpenReclassifyModal(file)}
            onOpenEditModal={handleEditModalOpen}
            onOpenPermissionsModal={setPermissionsFile}
            onOpenViewedStatusModal={setViewedStatusFile}
            externalModalControl={true}
            isSelected={selectedFiles.some(sf => sf.id === file.id)}
            onSelect={toggleFileSelection}
            isDragSelecting={isDragSelecting}
            disableSelectionHighlight={true}
            searchQuery={searchQuery}
            searchMode={searchMode}
            forceMobileMenu={isMobile || isTouchDevice}
          />
        </DraggableFile>
      </GridItemContainer>
    </div>
  )
}

const CustomViews = ({
  onFolderSelect,
  onViewChange,
  selectedFolder,
  canEdit: canEditProp,
  searchCriteria,
  searchQuery,
  setSearchQuery,
  searchMode,
  selectedUploaders,
  setSelectedUploaders,
  selectedFileTypes,
  setSelectedFileTypes,
  selectedFileTags,
  setSelectedFileTags,
  selectedViewableBy = [],
  setSelectedViewableBy = () => {},
  onRemoveFilter,
  onClearAllFilters,
  showOnlyMyFiles,
  ...otherProps
}) => {
  const dispatch = useDispatch()
  const bp = useSelector(getSelectedBp)
  const currentUser = useSelector(getCurrentUser)
  const fileTypes = useSelector(getFileTypes)
  const fileCategories = useSelector(selectFileCategories)
  const folders = useSelector(selectFolders)
  const reduxBpFiles = useSelector(selectBpFilesList) // Get files from Redux store
  const reduxSearchResults = useSelector(localSearchFileResults) // Shared search results from saga
  const reduxIsSearching = useSelector(selectIsSearchLoading) // Shared search loading state
  const isSearchTruncated = useSelector(localSearchTruncated) // Whether results are capped at 300
  const customFolderFilesMap = useSelector(selectCustomFolderFiles) // Get folder->files mapping
  const loadedCustomFolders = useSelector(
    state => state.files.loadedCustomFolders || new Set()
  )
  const loadingCustomFolders = useSelector(
    state => state.files.loadingCustomFolders || new Set()
  )
  // Note: Don't use allFilesLoaded - that's for automatic view only
  const canEdit = canEditBp(currentUser, bp)

  // Track which folders have been fully enriched (files ready for display)
  const [enrichedFolders, setEnrichedFolders] = useState(new Set())

  // Core state
  const [customViews, setCustomViews] = useState([])
  const [selectedView, setSelectedView] = useState(null)
  const [initialLoading, setInitialLoading] = useState(true)

  // Bulk action modals
  const [bulkShareModalOpen, setBulkShareModalOpen] = useState(false)
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false)
  const [bulkUnshareModalOpen, setBulkUnshareModalOpen] = useState(false)

  // Wrapper function to save custom view ID to cookies
  const setSelectedViewWithCookie = view => {
    // Clear existing data when switching views
    if (view?.id !== selectedView?.id) {
      setTreeData(null)
      setUnfilteredTreeData(null)
      setSelectedNode(null)
      setSelectedFiles([])
      dispatch(searchLocalFilesRoutine.success([]))
      setExpandedFolders(new Set())

      // Invalidate all custom folder caches when switching views
      // This ensures fresh data is loaded for the new view
      dispatch(invalidateCustomFolderCacheRoutine({ invalidateAll: true }))
    }

    setSelectedView(view)
    // Notify parent of the active view (for SharePoint search filtering)
    if (onViewChange) {
      onViewChange(view?.integrationType === 'sharepoint' ? view.id : null)
    }
    if (view) {
      CookieService.setCustomViewId(bp.id, view.id)
    }
  }
  const [treeData, setTreeData] = useState(null)
  const [unfilteredTreeData, setUnfilteredTreeData] = useState(null) // Store unfiltered data for local filtering

  // Derive search state from Redux (shared with automatic view via saga)
  const hasActiveSearch = useMemo(() => {
    const minChars = searchMode === 'content' ? 6 : 3
    return (searchQuery && searchQuery.trim().length >= minChars) ||
      (selectedUploaders && selectedUploaders.length > 0) ||
      (selectedFileTypes && selectedFileTypes.length > 0) ||
      (selectedFileTags && selectedFileTags.length > 0) ||
      showOnlyMyFiles
  }, [searchQuery, searchMode, selectedUploaders, selectedFileTypes, selectedFileTags, showOnlyMyFiles])

  // Preserve null/array semantics: null = no search, [] = no matches, [...] = results
  const searchResults = hasActiveSearch ? reduxSearchResults : null
  const searchLoading = reduxIsSearching

  const [selectedNode, setSelectedNode] = useState(selectedFolder)
  const [loading, setLoading] = useState(false)
  // Progressive loading - load tree structure first (fast), then files (slow)
  const [filesMap, setFilesMap] = useState(new Map()) // Map of file_id -> full file object
  const [filesLoading, setFilesLoading] = useState(false)
  const [filesLoaded, setFilesLoaded] = useState(false)
  const [viewMode, setViewMode] = useState('grid')
  const [sortBy, setSortBy] = useState('newest')
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState(null)
  const [sortBottomSheetOpen, setSortBottomSheetOpen] = useState(false)
  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null)
  const [actionsBottomSheetOpen, setActionsBottomSheetOpen] = useState(false)
  const [breadcrumb, setBreadcrumb] = useState([])
  const [refreshKey, setRefreshKey] = useState(0) // To force re-renders after moves
  const [selectedFile, setSelectedFile] = useState(null) // For file preview
  const [selectedFiles, setSelectedFiles] = useState([]) // Multi-select state
  const selectionAnchorRef = useRef(null)
  const [isSwitchingView, setIsSwitchingView] = useState(false) // To prevent showing old data during view switch

  const [isDragSelecting, setIsDragSelecting] = useState(false) // Track drag selection
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 }) // Start point for drag selection
  const [dragEnd, setDragEnd] = useState({ x: 0, y: 0 }) // End point for drag selection
  const [dragScrollOffset, setDragScrollOffset] = useState({ x: 0, y: 0 })
  const [dragViewport, setDragViewport] = useState({ left: 0, top: 0, width: 0, height: 0 })
  const containerRef = useRef(null) // Reference to the main container for drag selection
  const virtualizedScrollRef = useRef(null)
  const isMouseDownRef = useRef(false)
  const isDragSelectingRef = useRef(false) // Ref to avoid re-renders
  const dragStartRef = useRef({ x: 0, y: 0 })
  const selectedDuringDragRef = useRef(new Set())
  const dragSelectionBaseRef = useRef([])
  const isAdditiveDragRef = useRef(false)
  const dragCardRectsRef = useRef(new Map())
  const lastDragPointerRef = useRef({ x: 0, y: 0 })
  const autoScrollFrameRef = useRef(null)
  const suppressNextContentClickRef = useRef(false)
  const [expandedFolders, setExpandedFolders] = useState(new Set()) // Track expanded folders
  const [isUpdatingAfterDrop, setIsUpdatingAfterDrop] = useState(false) // Flag to prevent interference during drop updates
  const folderLabel = selectedNode?.name || 'this folder'
  const previousFolderRef = useRef(null) // Store folder before search starts
  const [shouldReloadTree, setShouldReloadTree] = useState(false) // Flag to trigger tree reload
  const lastRequestedFolderRef = useRef(null) // Tracks the last folder we asked the saga to load (prevents duplicates)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false) // Mobile drawer state for custom views
  const menuSlot = useFloatingSlot('views-menu', FLOATING_PRIORITY.menu)
  const isTouchDevice =
    typeof window !== 'undefined' &&
    ((window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ||
      (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) ||
      ('ontouchstart' in window))

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editingFile, setEditingFile] = useState(null)
  const [permissionsFile, setPermissionsFile] = useState(null)
  const [viewedStatusFile, setViewedStatusFile] = useState(null)

  // Modal state management - lifted from File component to prevent loss on re-render
  const [reclassifyModalState, setReclassifyModalState] = useState({
    open: false,
    file: null
  })

  // SharePoint integration modal state
  const [sharePointModalOpen, setSharePointModalOpen] = useState(false)
  const [sharePointAccessModalOpen, setSharePointAccessModalOpen] =
    useState(false)
  const [selectedSharePointView, setSelectedSharePointView] = useState(null)
  const [sharePointReauthModalOpen, setSharePointReauthModalOpen] = useState(false)
  const [sharePointReauthCustomViewId, setSharePointReauthCustomViewId] = useState(null)

  const handleOpenReclassifyModal = useCallback(file => {
    setReclassifyModalState({ open: true, file })
  }, [])

  const handleCloseReclassifyModal = useCallback(() => {
    setReclassifyModalState({ open: false, file: null })
  }, [])

  const handleFilePreview = useCallback(file => {
    setSelectedFile(file)
  }, [])

  // Color palette - must match FileCategories.js and UnifiedFiles.js
  const colorPalette = [
    '#0c132b',
    '#bbeaf0',
    '#36becd',
    '#ffc111',
    '#cd2000',
    '#ffaf7f'
  ]

  const selectedFileIds = useMemo(
    () => [...new Set(selectedFiles.map(f => f.id))],
    [selectedFiles]
  )

  // Get category color based on folder index (same logic as automatic view)
  const getCategoryColor = useCallback(
    file => {
      const fileCategoryId = file?.fileType?.fileCategoryId
      if (!fileCategoryId || !fileCategories || !folders) {
        return '#ccc' // Default fallback
      }

      // Find which category the file belongs to
      const fileCategory = fileCategories.find(cat => cat.id === fileCategoryId)
      if (!fileCategory) {
        return '#ccc'
      }

      // Find the folder index using the same logic as FileCategories.js
      const folderIndex = folders.findIndex(
        folder => folder.id === fileCategory.folderId
      )
      if (folderIndex !== -1) {
        return colorPalette[folderIndex % colorPalette.length]
      }

      return '#ccc' // Default fallback
    },
    [fileCategories, folders, colorPalette]
  )
  const [showingSearchResults, setShowingSearchResults] = useState(false) // Track when showing search results instead of folder contents
  const [folderModalOpen, setFolderModalOpen] = useState(false)
  const [folderModalParentId, setFolderModalParentId] = useState(null)
  const [newFolderName, setNewFolderName] = useState('')
  const [editFolderModalOpen, setEditFolderModalOpen] = useState(false)
  const [editingFolder, setEditingFolder] = useState(null)
  const [editFolderName, setEditFolderName] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [folderToDelete, setFolderToDelete] = useState(null)
  const [createViewModalOpen, setCreateViewModalOpen] = useState(false)
  const [newViewName, setNewViewName] = useState('')
  const [editViewModalOpen, setEditViewModalOpen] = useState(false)
  const [viewToEdit, setViewToEdit] = useState(null)
  const [editViewName, setEditViewName] = useState('')
  const [deleteViewModalOpen, setDeleteViewModalOpen] = useState(false)
  const [viewToDelete, setViewToDelete] = useState(null)
  const [viewActionLoading, setViewActionLoading] = useState(false)

  const api = APIService()

  const handleEditModalOpen = fileToEdit => {
    setEditingFile(fileToEdit)
    setEditModalOpen(true)
  }

  // Load custom views on mount
  useEffect(() => {
    if (bp?.id) {
      loadCustomViews()
    }
  }, [bp?.id])

  // Auto-switch to relevance sorting when content search results arrive
  useEffect(() => {
    if (searchResults && searchResults.length > 0) {
      const hasSearchScores = searchResults.some(f => f.searchScore !== undefined)
      if (hasSearchScores && sortBy === 'newest') {
        setSortBy('relevance')
      }
    }
  }, [searchResults])

  // Reset to 'newest' sorting when switching from content to name mode
  useEffect(() => {
    if (searchMode === 'name' && sortBy === 'relevance') {
      setSortBy('newest')
    }
  }, [searchMode])

  // Restore expanded folders when view changes and tree data is loaded
  useEffect(() => {
    if (bp?.id && selectedView?.id && treeData) {
      // Use localStorage instead of cookies to avoid 431 errors
      try {
        const savedExpandedFoldersStr = localStorage.getItem(
          `customExpandedFolders_${bp.id}_${selectedView.id}`
        )
        const savedExpandedFolders = savedExpandedFoldersStr
          ? JSON.parse(savedExpandedFoldersStr)
          : null
        console.log(
          'Restoring custom expanded folders:',
          savedExpandedFolders,
          'for view:',
          selectedView.id
        )
        if (savedExpandedFolders) {
          console.log('Setting expanded folders to:', savedExpandedFolders)
          if (Array.isArray(savedExpandedFolders)) {
            setExpandedFolders(new Set(savedExpandedFolders))

            // REMOVED: Auto-loading of folders to prevent timeout on large datasets
            // Files will only load when user manually clicks to expand a folder
          } else {
            console.log(
              'Unexpected saved expanded folders format:',
              typeof savedExpandedFolders,
              savedExpandedFolders
            )
            setExpandedFolders(new Set())
          }
        } else {
          console.log('No saved expanded folders, resetting to empty')
          setExpandedFolders(new Set())
        }
      } catch (e) {
        console.warn('Failed to restore expanded folders from localStorage:', e)
        setExpandedFolders(new Set())
      }
    }
  }, [bp?.id, selectedView?.id, treeData])

  // Load tree structure when view changes and poll for SharePoint sync status
  useEffect(() => {
    let syncInterval = null
    let initialSyncTimeout = null

    if (selectedView) {
      loadTreeStructure()

      // Only poll for SharePoint sync when the view's sync status indicates it
      // is actively pending/running. Otherwise we'd hammer the API forever.
      const initialSyncStatus =
        selectedView.syncStatus?.status || selectedView.sync_status?.status
      const needsPolling =
        selectedView.integrationType === 'sharepoint' &&
        (initialSyncStatus === 'pending' || initialSyncStatus === 'running')

      if (needsPolling) {
        const checkSyncStatus = async () => {
          try {
            const response = await api.get(
              `/building-passport/${bp.id}/custom-views/${selectedView.id}`
            )
            const updatedView = response.data
            const syncStatus = updatedView.syncStatus || updatedView.sync_status
            if (
              syncStatus?.status &&
              syncStatus.status !== 'pending' &&
              syncStatus.status !== 'running'
            ) {
              loadTreeStructure(false)
              if (syncInterval) clearInterval(syncInterval)
            }
          } catch (error) {
            console.error('Error checking sync status:', error)
          }
        }

        syncInterval = setInterval(checkSyncStatus, 3000)
        initialSyncTimeout = setTimeout(checkSyncStatus, 2000)
      }
    }

    return () => {
      if (syncInterval) clearInterval(syncInterval)
      if (initialSyncTimeout) clearTimeout(initialSyncTimeout)
    }
  }, [selectedView?.id])

  // Update showingSearchResults state based on search criteria
  const prevShowingRef = useRef(false)
  const mountedRef = useRef(false)
  const nodeBeforeSearchRef = useRef(null)
  useEffect(() => {
    const shouldShow = hasActiveSearch && (searchResults !== null || searchLoading)

    // When search activates, deselect folder so all results show unfiltered
    if (shouldShow && !prevShowingRef.current) {
      nodeBeforeSearchRef.current = selectedNode
      setSelectedNode(null)
      setBreadcrumb([])
    }

    // When search is cleared, restore previous folder and reload tree
    if (!shouldShow && prevShowingRef.current && mountedRef.current && selectedView) {
      setShouldReloadTree(true)
      if (nodeBeforeSearchRef.current) {
        setSelectedNode(nodeBeforeSearchRef.current)
        setBreadcrumb(buildFolderPath(nodeBeforeSearchRef.current, treeData))
        nodeBeforeSearchRef.current = null
      }
    }

    prevShowingRef.current = shouldShow
    setShowingSearchResults(shouldShow)
  }, [
    hasActiveSearch,
    searchResults,
    searchLoading,
    selectedView
  ])

  // Mark as mounted after the first render cycle (skip initial clear-effect churn)
  useEffect(() => {
    const timer = setTimeout(() => { mountedRef.current = true }, 500)
    return () => clearTimeout(timer)
  }, [])

  // Clear search when switching between views so each tab starts fresh
  useEffect(() => {
    if (selectedView) {
      dispatch(searchLocalFilesRoutine.success([]))
      if (onClearAllFilters) {
        onClearAllFilters()
      }
    }
  }, [selectedView])

  // Update selected node when selectedFolder prop changes (but not during drop updates)
  useEffect(() => {
    if (!isUpdatingAfterDrop && selectedFolder !== selectedNode) {
      // Ignore automatic view folders (like uncategorised, all_files) - they don't exist in custom views
      if (
        selectedFolder?.id === 'uncategorised' ||
        selectedFolder?.id === 'all_files'
      ) {
        return
      }

      setSelectedNode(selectedFolder)
      if (selectedFolder) {
        setBreadcrumb(buildFolderPath(selectedFolder, treeData))
      } else {
        setBreadcrumb([])
      }
    }
  }, [selectedFolder, selectedNode, treeData, isUpdatingAfterDrop])

  const loadCustomViews = async () => {
    try {
      const response = await api.get(`/building-passport/${bp.id}/custom-views`)
      setCustomViews(response.data)
      if (response.data.length > 0) {
        // Try to restore saved view first, otherwise use first available
        const savedViewId = CookieService.getCustomViewId(bp.id)
        const viewToSelect = savedViewId
          ? response.data.find(view => view.id === savedViewId) ||
            response.data[0]
          : response.data[0]
        setSelectedViewWithCookie(viewToSelect)
      }
    } catch (error) {
      console.error('Failed to load custom views:', error)
    } finally {
      setInitialLoading(false)
    }
  }

  // Search is dispatched by StreamlinedFilesSearch (shared search bar) which receives
  // customViewId for SharePoint views. No duplicate dispatch needed here.

  // Re-run search with unlimited flag to bypass the 300 result cap
  const handleLoadAllResults = useCallback(() => {
    if (!bp?.id || !hasActiveSearch) return

    let effectiveUploaderIds = selectedUploaders || []
    if (showOnlyMyFiles && currentUser?.id && effectiveUploaderIds.length === 0) {
      effectiveUploaderIds = [currentUser.id]
    }

    dispatch(searchLocalFilesRoutine({
      query: searchQuery?.trim() || '',
      uploaderIds: effectiveUploaderIds,
      fileTypeIds: selectedFileTypes || [],
      fileTagIds: selectedFileTags || [],
      searchMode: searchMode,
      bpId: bp.id,
      unlimited: true,
      ...(selectedView?.integrationType === 'sharepoint' ? { customViewId: selectedView.id } : {}),
      ...(selectedViewableBy?.length > 0 ? { viewableByUserId: selectedViewableBy[0] } : {})
    }))
  }, [hasActiveSearch, searchQuery, searchMode, selectedUploaders, selectedFileTypes, selectedFileTags, showOnlyMyFiles, currentUser?.id, bp?.id, selectedView, selectedViewableBy, dispatch])

  // Listen for SharePoint sync completion events
  useEffect(() => {
    const handleSharePointSyncComplete = event => {
      const { customViewId, buildingPassportId } = event.detail
      if (customViewId === selectedView?.id && buildingPassportId === bp?.id) {
        console.log('SharePoint sync completed, invalidating caches and reloading tree structure')
        // Invalidate all folder caches for this view since sync may have added/changed files
        dispatch(invalidateCustomFolderCacheRoutine({ invalidateAll: true }))
        loadTreeStructure(false)
        setRefreshKey(prev => prev + 1)
      }
    }

    window.addEventListener(
      'sharepoint-sync-complete',
      handleSharePointSyncComplete
    )
    return () =>
      window.removeEventListener(
        'sharepoint-sync-complete',
        handleSharePointSyncComplete
      )
  }, [selectedView?.id, bp?.id])

  // Helper function: Enriches tree structure with full file objects
  // Matches file_id from tree-structure with full file data from /files endpoint
  const enrichTreeWithFiles = useCallback((treeData, filesMap) => {
    const enrichFiles = (fileRefs, folderName = 'unknown') => {
      if (!fileRefs || !Array.isArray(fileRefs)) return []

      return fileRefs.map(fileRef => {
        // New optimized format: { file_id: "...", assignment: {...} } or { fileId: "...", assignment: {...} }
        const fileId = fileRef.file_id || fileRef.fileId || fileRef.id
        const fullFile = filesMap.get(fileId)

        if (!fullFile) {
          // File not loaded yet - keep the reference for later enrichment
          return fileRef
        }

        // Merge full file data with assignment metadata
        return {
          ...fullFile,
          assignment: fileRef.assignment || null
        }
      })
    }

    const enrichFolder = folder => {
      return {
        ...folder,
        files: enrichFiles(folder.files, folder.name || folder.id),
        children: folder.children ? folder.children.map(enrichFolder) : []
      }
    }

    return {
      folders: treeData.folders ? treeData.folders.map(enrichFolder) : [],
      unorganized: treeData.unorganized
        ? {
            ...treeData.unorganized,
            files: enrichFiles(treeData.unorganized.files, 'Unassigned')
          }
        : null,
      rootFiles: enrichFiles(treeData.rootFiles || [], 'Root')
    }
  }, [])

  // Compute enrichedFolders (green checkmarks) based on Redux data instead of tree enrichment
  useEffect(() => {
    if (!treeData || !selectedView) {
      return
    }

    // Compute enrichedFolders based on loadedCustomFolders and customFolderFilesMap
    // A folder is "enriched" (shows green checkmark) if it's been loaded
    const newEnriched = new Set()

    for (const folderId of loadedCustomFolders) {
      newEnriched.add(folderId)
    }

    setEnrichedFolders(newEnriched)
  }, [treeData, selectedView, loadedCustomFolders, customFolderFilesMap])

  // OLD enrichment logic - kept for reference but disabled
  useEffect(() => {
    return

    if (!treeData) {
      console.log('⚠️ No treeData, skipping enrichment')
      return
    }
    if (!reduxBpFiles || reduxBpFiles.length === 0) {
      console.log('⚠️ No Redux files, skipping enrichment')
      return
    }
    if (!selectedView) {
      console.log('⚠️ No selectedView, skipping enrichment')
      return
    }

    console.log('🔄 Enrichment effect triggered', {
      loadedCustomFolders: Array.from(loadedCustomFolders),
      reduxBpFilesCount: reduxBpFiles.length,
      hasTreeData: !!treeData,
      selectedViewId: selectedView?.id,
      selectedViewIntegrationType: selectedView?.integrationType
    })

    // Only enrich if we have actually loaded files for custom view
    // Don't use allFilesLoaded - that's from automatic view!
    if (loadedCustomFolders.size === 0) {
      console.log('⚠️ No loaded custom folders, skipping enrichment')
      return
    }

    // Check if tree is already enriched to prevent infinite loops
    // If the first file in any folder is an object (not just a number ID), it's already enriched
    const isAlreadyEnriched = () => {
      // Check folders
      if (treeData.folders && treeData.folders.length > 0) {
        for (const folder of treeData.folders) {
          if (folder.files && folder.files.length > 0) {
            const firstFile = folder.files[0]
            // If it's an object with displayName/display_name, it's enriched
            if (
              typeof firstFile === 'object' &&
              (firstFile.displayName || firstFile.display_name)
            ) {
              return true
            }
            // If it's just a number, needs enrichment
            return false
          }
        }
      }
      // Check unorganized
      if (
        treeData.unorganized?.files &&
        treeData.unorganized.files.length > 0
      ) {
        const firstFile = treeData.unorganized.files[0]
        if (
          typeof firstFile === 'object' &&
          (firstFile.displayName || firstFile.display_name)
        ) {
          return true
        }
        return false
      }
      // No files to check, consider not enriched
      return false
    }

    // Check if tree needs enrichment
    // Only enrich if we have files that aren't already in the tree
    const needsEnrichment = () => {
      // Check if any loaded folder has unenriched files
      for (const folderId of loadedCustomFolders) {
        // Find folder in tree
        const findFolder = (folders, id) => {
          for (const folder of folders) {
            if (folder.id === id) return folder
            if (folder.children) {
              const found = findFolder(folder.children, id)
              if (found) return found
            }
          }
          return null
        }

        const folder =
          folderId === 'unorganized'
            ? treeData.unorganized
            : findFolder(treeData.folders || [], folderId)

        if (folder && folder.files && folder.files.length > 0) {
          const firstFile = folder.files[0]
          // If file is just a reference (has fileId but no displayName), needs enrichment
          if (
            typeof firstFile === 'object' &&
            firstFile.fileId &&
            !firstFile.displayName &&
            !firstFile.display_name
          ) {
            return true
          }
        }
      }
      return false
    }

    let workingTree = treeData

    if (needsEnrichment()) {
      console.log('✅ Enriching tree with', reduxBpFiles.length, 'files')

      // Create a map of file IDs to full file objects
      const filesMap = new Map(reduxBpFiles.map(file => [file.id, file]))

      // Enrich tree structure with full file data
      const enrichedTree = enrichTreeWithFiles(treeData, filesMap)

      // Update treeData with enriched version
      setTreeData(enrichedTree)

      // Also update unfilteredTreeData
      setUnfilteredTreeData(enrichedTree)

      workingTree = enrichedTree
    } else {
      console.log('⚠️ Tree already enriched, skipping')
    }

    // ALWAYS compute enrichedFolders based on which folders have displayable files
    // This determines which folders show the green checkmark
    const newEnriched = new Set()

    const checkFolder = folder => {
      if (!folder) return

      const folderKey = folder.id === 'unorganized' ? 'unorganized' : folder.id
      const folderName = folder.name || folder.id

      // Check if this specific folder is loaded and add checkmark if so
      const isFolderLoaded = loadedCustomFolders.has(folderKey)

      if (!isFolderLoaded) {
        console.log(
          `⚠️ Folder ${folderName} (${folderKey}) not in loadedCustomFolders, skipping checkmark (but checking children)`
        )
        // Don't return - still check children
      }

      // Only process checkmark logic if folder is loaded
      if (isFolderLoaded) {
        const hasFilesInTree = folder.files && folder.files.length > 0

        if (!hasFilesInTree) {
          // No files in tree - mark as enriched (empty folder is fully loaded)
          console.log(
            `✅ Folder ${folder.name} (${folderKey}) has no files, marking as enriched`
          )
          newEnriched.add(folderKey)
        } else {
          // Has files in tree - check if ALL are displayable (enriched)
          const allFilesEnriched = folder.files.every(file => {
            const hasName = !!(
              file.displayName ||
              file.display_name ||
              file.name
            )
            const hasFileData = !!(file.url || file.versions?.length > 0 || file.isIdentityRestricted)
            return hasName && hasFileData
          })

          console.log(
            `🔍 Folder ${folder.name} (${folderKey}) has ${folder.files.length} files, allFilesEnriched: ${allFilesEnriched}`
          )

          if (allFilesEnriched) {
            // ALL files are enriched and displayable
            console.log(
              `✅ Folder ${folder.name} (${folderKey}) all files enriched, marking as enriched`
            )
            newEnriched.add(folderKey)
          } else {
            console.log(
              `❌ Folder ${folder.name} (${folderKey}) has unenriched files:`,
              folder.files.filter(file => {
                const hasName = !!(
                  file.displayName ||
                  file.display_name ||
                  file.name
                )
                const hasFileData = !!(file.url || file.versions?.length > 0 || file.isIdentityRestricted)
                return !(hasName && hasFileData)
              })
            )
          }
        }
      }

      // Recursively check children
      if (folder.children) {
        folder.children.forEach(checkFolder)
      }
    }

    // Check all folders
    if (workingTree.folders) {
      console.log(
        '🔍 Checking folders:',
        workingTree.folders.map(f => `${f.name} (${f.id})`)
      )
      workingTree.folders.forEach(checkFolder)
    }

    // Check unorganized
    if (workingTree.unorganized) {
      checkFolder(workingTree.unorganized)
    }

    // Check SharePoint root files
    // Note: SharePoint root can be either:
    // 1. A virtual folder with files in treeData.rootFiles
    // 2. An existing database folder with id 'sharepoint-root' or matching the root folder name
    if (loadedCustomFolders.has('sharepoint-root')) {
      // Find the sharepoint-root folder in the tree (it could be the first folder)
      let rootFolder = null
      if (workingTree.folders && workingTree.folders.length > 0) {
        // Check if first folder has sharepoint-root id or check all folders
        rootFolder = workingTree.folders.find(f => f.id === 'sharepoint-root')
        // If not found by ID, check if first folder is the root (SharePoint usually has one root)
        if (!rootFolder && selectedView?.integrationType === 'sharepoint') {
          rootFolder = workingTree.folders[0]
        }
      }

      // Check files in either rootFiles OR the root folder's files
      const rootFiles =
        workingTree.rootFiles && workingTree.rootFiles.length > 0
          ? workingTree.rootFiles
          : rootFolder && rootFolder.files
            ? rootFolder.files
            : []

      if (rootFiles.length === 0) {
        // No root files - mark as enriched (empty root is fully loaded)
        console.log('✅ SharePoint root has no files, marking as enriched')
        newEnriched.add('sharepoint-root')
      } else {
        // Has root files - check if ALL are displayable (enriched)
        const allRootFilesEnriched = rootFiles.every(file => {
          const hasName = !!(file.displayName || file.display_name || file.name)
          const hasFileData = !!(file.url || file.versions?.length > 0 || file.isIdentityRestricted)
          return hasName && hasFileData
        })

        console.log(
          `🔍 SharePoint root has ${rootFiles.length} files, allRootFilesEnriched: ${allRootFilesEnriched}`
        )

        if (allRootFilesEnriched) {
          console.log(
            '✅ SharePoint root all files enriched, marking as enriched'
          )
          newEnriched.add('sharepoint-root')
        } else {
          console.log(
            '❌ SharePoint root has unenriched files:',
            rootFiles.filter(file => {
              const hasName = !!(
                file.displayName ||
                file.display_name ||
                file.name
              )
              const hasFileData = !!(file.url || file.versions?.length > 0 || file.isIdentityRestricted)
              return !(hasName && hasFileData)
            })
          )
        }
      }
    }

    console.log('✅ Setting enrichedFolders:', Array.from(newEnriched))
    console.log('📊 loadedCustomFolders:', Array.from(loadedCustomFolders))
    setEnrichedFolders(newEnriched)
  }, [
    treeData,
    loadedCustomFolders,
    reduxBpFiles,
    enrichTreeWithFiles,
    selectedView
  ])

  const loadTreeStructure = async (
    updateSelectedNode = false,
    forceRefresh = false
  ) => {
    if (!selectedView) return

    // Clear existing data first to ensure fresh load
    setTreeData(null)
    setLoading(true)
    setFilesLoading(true)
    setFilesLoaded(false)

    try {
      // Build URL with viewable_by filter if selected
      let baseUrl = `/building-passport/${bp.id}/custom-views/${selectedView.id}/tree-structure`
      const params = new URLSearchParams()

      if (forceRefresh) {
        params.append('t', new Date().getTime())
      }

      if (selectedViewableBy && selectedViewableBy.length > 0) {
        params.append('viewable_by_user_id', selectedViewableBy[0])
      }

      const url = params.toString()
        ? `${baseUrl}?${params.toString()}`
        : baseUrl

      // Load tree structure first (fast)
      const treeResponse = await api.get(url)

      // Process tree structure immediately (this is fast - only file IDs)
      if (treeResponse) {
        const rawTreeData = treeResponse.data

        // USE REDUX FILES INSTEAD OF DIRECT API CALL
        // This eliminates duplicate file loading between automatic and custom views
        // Files from Redux are already filtered by backend FilePolicy (includes folder permissions)
        let allFiles = reduxBpFiles || []

        // Note: viewable_by filtering is handled by backend ShowFilesList endpoint
        // which properly applies FilePolicy including folder permissions, access_type, etc.
        // No frontend filtering needed - backend handles all permission checks correctly

        // Create files map from Redux files (for later enrichment when folder clicked)
        let currentFilesMap = new Map()
        allFiles.forEach(file => {
          currentFilesMap.set(file.id, file)
        })
        setFilesMap(currentFilesMap)

        // DON'T enrich tree with files - just store structure with file IDs and counts
        // Files will be enriched on-demand when user clicks a folder
        const processedData = {
          folders: rawTreeData.folders || [],
          unorganized: rawTreeData.unorganized
            ? {
                ...rawTreeData.unorganized,
                id: 'unorganized',
                name: 'Unassigned',
                fileCount:
                  rawTreeData.unorganized.fileCount ||
                  rawTreeData.unorganized.file_count ||
                  rawTreeData.unorganized.files?.length ||
                  0,
                files: rawTreeData.unorganized.files || [] // Keep as file IDs only
              }
            : null,
          rootFiles: rawTreeData.rootFiles || rawTreeData.root_files || []
        }

        // For SharePoint views, don't show the unorganized folder as it has its own folder structure
        if (selectedView?.integrationType === 'sharepoint') {
          setTreeData({
            ...processedData,
            unorganized: null // Hide unorganized folder for SharePoint views
          })
        } else {
          setTreeData(processedData)
        }
        setUnfilteredTreeData(processedData) // Store unfiltered data

        // Auto-select saved folder from localStorage
        if (!updateSelectedNode && !selectedNode && selectedView) {
          const savedSubfolderId = CookieService.getLastSubfolder(
            bp.id,
            selectedView.id
          )
          if (savedSubfolderId) {
            const findSavedNode = (folders, nodeId) => {
              for (const folder of folders) {
                if (folder.id === nodeId) {
                  return folder
                }
                if (folder.children) {
                  const found = findSavedNode(folder.children, nodeId)
                  if (found) return found
                }
              }
              return null
            }

            let savedNode = null
            if (
              savedSubfolderId === 'unorganized' &&
              processedData.unorganized
            ) {
              savedNode = processedData.unorganized
            } else if (processedData.folders) {
              savedNode = findSavedNode(processedData.folders, savedSubfolderId)
            }

            if (savedNode) {
              console.log('Restored saved subfolder:', savedNode.id)

              // Trigger file loading BEFORE setting selected node to ensure loading state is set first
              if (savedNode.id === 'unorganized') {
                if (!loadedCustomFolders.has('unorganized')) {
                  console.log(
                    `📥 Loading files for restored Unassigned folder...`
                  )
                  dispatch(
                    loadCustomFolderFilesRoutine({
                      bpId: bp.id,
                      customViewId: selectedView.id,
                      customFolderId: null
                    })
                  )
                }
              } else if (
                savedNode.id &&
                savedNode.id.match(
                  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
                )
              ) {
                if (!loadedCustomFolders.has(savedNode.id)) {
                  console.log(
                    `📥 Loading files for restored ${selectedView.integrationType === 'sharepoint' ? 'SharePoint' : 'custom'} folder: ${savedNode.name}`
                  )
                  dispatch(
                    loadCustomFolderFilesRoutine({
                      bpId: bp.id,
                      customViewId: selectedView.id,
                      customFolderId: savedNode.id
                    })
                  )
                }
              }

              // Set selected node AFTER dispatching load action
              setSelectedNode(savedNode)
              setBreadcrumb(buildFolderPath(savedNode, processedData))
              if (onFolderSelect && savedNode.type !== 'file') {
                onFolderSelect(savedNode)
              }
            } else {
              // No saved folder found, auto-select the first folder
              const firstFolder = processedData.folders?.[0]
              if (firstFolder) {
                console.log('Auto-selecting first folder:', firstFolder.name)
                setSelectedNode(firstFolder)
                setBreadcrumb(buildFolderPath(firstFolder, processedData))
                if (onFolderSelect && firstFolder.type !== 'file') {
                  onFolderSelect(firstFolder)
                }
                // Save to cookies
                CookieService.setLastSubfolder(
                  bp.id,
                  selectedView.id,
                  firstFolder.id
                )
                // Expand the first folder in the tree
                setExpandedFolders(prev => {
                  const newExpanded = new Set(prev)
                  newExpanded.add(firstFolder.id)
                  return newExpanded
                })
                // Trigger file loading for this folder
                if (
                  firstFolder.id &&
                  firstFolder.id.match(
                    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
                  )
                ) {
                  if (!loadedCustomFolders.has(firstFolder.id)) {
                    console.log(
                      `📥 Loading files for first ${selectedView.integrationType === 'sharepoint' ? 'SharePoint' : 'custom'} folder: ${firstFolder.name}`
                    )
                    dispatch(
                      loadCustomFolderFilesRoutine({
                        bpId: bp.id,
                        customViewId: selectedView.id,
                        customFolderId: firstFolder.id
                      })
                    )
                  }
                }
              }
            }
          } else {
            // No saved subfolder ID at all, auto-select the first folder
            const firstFolder = processedData.folders?.[0]
            if (firstFolder) {
              console.log(
                'Auto-selecting first folder (no saved ID):',
                firstFolder.name
              )
              setSelectedNode(firstFolder)
              setBreadcrumb(buildFolderPath(firstFolder, processedData))
              if (onFolderSelect && firstFolder.type !== 'file') {
                onFolderSelect(firstFolder)
              }
              // Save to cookies
              CookieService.setLastSubfolder(
                bp.id,
                selectedView.id,
                firstFolder.id
              )
              // Expand the first folder in the tree
              setExpandedFolders(prev => {
                const newExpanded = new Set(prev)
                newExpanded.add(firstFolder.id)
                return newExpanded
              })
              // Trigger file loading for this folder
              if (
                firstFolder.id &&
                firstFolder.id.match(
                  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
                )
              ) {
                if (!loadedCustomFolders.has(firstFolder.id)) {
                  if (selectedView.integrationType === 'sharepoint') {
                    console.log(
                      `📥 Loading files for first SharePoint folder: ${firstFolder.name}`
                    )
                    const files = (firstFolder.files || []).filter(
                      f => typeof f === 'object' && f.id
                    )
                    dispatch(
                      loadCustomFolderFilesRoutine.request({
                        bpId: bp.id,
                        customViewId: selectedView.id,
                        customFolderId: firstFolder.id
                      })
                    )
                    setTimeout(() => {
                      dispatch(
                        loadCustomFolderFilesRoutine.success({
                          customFolderId: firstFolder.id,
                          files: files
                        })
                      )
                    }, 100)
                  } else {
                    console.log(
                      `📥 Loading files for first custom folder: ${firstFolder.name}`
                    )
                    dispatch(
                      loadCustomFolderFilesRoutine({
                        bpId: bp.id,
                        customViewId: selectedView.id,
                        customFolderId: firstFolder.id
                      })
                    )
                  }
                }
              }
            }
          }
        }

        // Update selected node with fresh data if requested
        if (updateSelectedNode && selectedNode) {
          const findUpdatedNode = (folders, nodeId) => {
            for (const folder of folders) {
              if (folder.id === nodeId) {
                return folder
              }
              if (folder.children) {
                const found = findUpdatedNode(folder.children, nodeId)
                if (found) return found
              }
            }
            return null
          }

          if (selectedNode.id === 'unorganized' && processedData.unorganized) {
            setSelectedNode(processedData.unorganized)
          } else if (processedData.folders) {
            const updatedNode = findUpdatedNode(
              processedData.folders,
              selectedNode.id
            )
            if (updatedNode) {
              setSelectedNode(updatedNode)
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to load tree structure:', error)
    } finally {
      setLoading(false)
      setFilesLoading(false)
    }
  }

  // Reload tree when flag is set (after search is cleared).
  // Deps intentionally only include `shouldReloadTree` so this doesn't re-fire
  // every time `loadedCustomFolders` updates from a folder load.
  useEffect(() => {
    if (shouldReloadTree && selectedView) {
      // Clear search-related states but NOT treeData (it will be refreshed by loadTreeStructure)
      dispatch(searchLocalFilesRoutine.success([]))
      setSelectedNode(null)
      setSelectedFile(null)
      setSelectedFiles([])
      setBreadcrumb([])

      loadTreeStructure(true, true)
      setShouldReloadTree(false)
    }
  }, [shouldReloadTree])

  const handleNodeSelect = node => {
    // Prevent node selection during drop updates
    if (isUpdatingAfterDrop) {
      return
    }

    // When a search is active and the user clicks a folder, just narrow the
    // displayed results — do NOT trigger a folder file load.
    if (showingSearchResults && node && node.type !== 'file') {
      setSelectedNode(node)
      setBreadcrumb(buildFolderPath(node, treeData))
      if (onFolderSelect) onFolderSelect(node)
      if (selectedView) {
        CookieService.setLastSubfolder(bp.id, selectedView.id, node.id)
      }
      return
    }

    // Guard: if we're already loading or have already requested this folder, skip the dispatch.
    // Tracks the last requested folder to prevent rapid duplicate dispatches.
    const folderKey = node?.id === 'unorganized' || node?.id === 'sharepoint-root'
      ? node.id
      : node?.id
    const alreadyRequested = lastRequestedFolderRef.current === folderKey
    const alreadyLoaded = folderKey && loadedCustomFolders.has(folderKey === 'unorganized' ? 'unorganized' : folderKey)

    // Load files BEFORE setting selected node to ensure loading state is set first
    if (node && node.type !== 'file' && node.id && selectedView && !alreadyRequested && !alreadyLoaded) {
      // Handle "Unassigned" folder - load files from custom view endpoint
      if (node.id === 'unorganized') {
        lastRequestedFolderRef.current = 'unorganized'
        dispatch(
          loadCustomFolderFilesRoutine({
            bpId: bp.id,
            customViewId: selectedView.id,
            customFolderId: null,
            cancelOthers: true
          })
        )
      }
      // Handle SharePoint root folder
      else if (node.id === 'sharepoint-root') {
        lastRequestedFolderRef.current = 'sharepoint-root'
        dispatch(
          loadCustomFolderFilesRoutine({
            bpId: bp.id,
            customViewId: selectedView.id,
            customFolderId: 'sharepoint-root',
            cancelOthers: true
          })
        )
      }
      // Handle real folders (with UUID)
      else if (
        node.id.match(
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
        )
      ) {
        lastRequestedFolderRef.current = node.id
        dispatch(
          loadCustomFolderFilesRoutine({
            bpId: bp.id,
            customViewId: selectedView.id,
            customFolderId: node.id,
            cancelOthers: true
          })
        )
      }
    }

    // Set selected node AFTER dispatching load action so Redux state updates first
    setSelectedNode(node)

    // Save selected subfolder to cookies
    if (node && node.type !== 'file' && selectedView) {
      CookieService.setLastSubfolder(bp.id, selectedView.id, node.id)
    }

    // If a folder is selected from the right side, expand it in the left tree
    if (node && node.type === 'folder') {
      setExpandedFolders(prev => {
        const newExpanded = new Set(prev)
        newExpanded.add(node.id)

        // Also expand all parent folders to make sure the path is visible
        const folderPath = buildFolderPath(node, treeData)
        folderPath
          .filter(folder => folder)
          .forEach(folder => {
            if (folder.id !== node.id) {
              // Don't add the node itself again
              newExpanded.add(folder.id)
            }
          })

        // Save to localStorage instead of cookies to avoid 431 errors
        if (bp?.id && selectedView?.id) {
          try {
            localStorage.setItem(
              `customExpandedFolders_${bp.id}_${selectedView.id}`,
              JSON.stringify([...newExpanded])
            )
          } catch (e) {
            console.warn('Failed to save expanded folders to localStorage:', e)
          }
        }

        return newExpanded
      })
    }

    if (onFolderSelect && node && node.type !== 'file') {
      // Only call onFolderSelect for folders, not files
      onFolderSelect(node)
    }
    if (node) {
      setBreadcrumb(buildFolderPath(node, treeData))
      // Set selected file for preview if the selected node is a file
      if (node.type === 'file') {
        setSelectedFile(node)
      } else {
        setSelectedFile(null)
      }
    } else {
      setBreadcrumb([])
      setSelectedFile(null)
    }
  }

  const handleFolderCreate = (parentFolderId = null) => {
    if (!selectedView) return

    setFolderModalParentId(parentFolderId)
    setNewFolderName('')
    setFolderModalOpen(true)
  }

  const handleFolderModalSubmit = async () => {
    if (!newFolderName.trim()) return

    try {
      setFolderModalOpen(false)
      const parentExistsInTree = folders =>
        (folders || []).some(
          f => f.id === folderModalParentId || parentExistsInTree(f.children)
        )
      const resolvedParentId =
        folderModalParentId && parentExistsInTree(treeData?.folders)
          ? folderModalParentId
          : null
      const response = await api.post(
        `/building-passport/${bp.id}/custom-views/${selectedView.id}/folders`,
        {
          name: newFolderName.trim(),
          parentFolderId: resolvedParentId
        }
      )

      // Wait for backend cache invalidation to complete (300ms delay)
      await new Promise(resolve => setTimeout(resolve, 300))

      // Force full refresh to bypass all caches
      setRefreshKey(prev => prev + 1)

      // Reload tree structure with force refresh to bypass cache
      await loadTreeStructure(false, true)

      // Auto-select the newly created folder and expand parents
      if (response.data && response.data.id) {
        // Find the newly created folder in the tree data
        const findFolderById = (folders, targetId) => {
          for (const folder of folders) {
            if (folder.id === targetId) {
              return folder
            }
            if (folder.children) {
              const found = findFolderById(folder.children, targetId)
              if (found) return found
            }
          }
          return null
        }

        // Find path to parent folders that need to be expanded
        const findPathToFolder = (folders, targetId, path = []) => {
          for (const folder of folders) {
            const currentPath = [...path, folder.id]
            if (folder.id === targetId) {
              return currentPath
            }
            if (folder.children) {
              const found = findPathToFolder(
                folder.children,
                targetId,
                currentPath
              )
              if (found) return found
            }
          }
          return null
        }

        // Give a moment for the tree to update, then select the new folder and expand parents
        setTimeout(() => {
          // Wait for the tree to be fully loaded, then find and select the new folder
          const selectNewFolder = () => {
            setTreeData(currentTreeData => {
              if (currentTreeData && currentTreeData.folders) {
                const newFolder = findFolderById(
                  currentTreeData.folders,
                  response.data.id
                )
                if (newFolder) {
                  // Auto-expand parent folders if this is a subfolder
                  if (folderModalParentId) {
                    const pathToParent = findPathToFolder(
                      currentTreeData.folders,
                      folderModalParentId
                    )
                    if (pathToParent) {
                      // Expand all folders in the path to the parent (excluding the new folder itself)
                      setExpandedFolders(prev => {
                        const newExpanded = new Set(prev)
                        pathToParent.forEach(folderId => {
                          newExpanded.add(folderId)
                        })
                        return newExpanded
                      })
                    }
                  }

                  // Use a separate timeout to ensure the tree data state is updated before calculating breadcrumb
                  setTimeout(() => {
                    setSelectedNode(newFolder)
                    if (onFolderSelect) {
                      onFolderSelect(newFolder)
                    }
                    setBreadcrumb(buildFolderPath(newFolder, currentTreeData))
                  }, 50)
                }
              }
              return currentTreeData
            })
          }

          selectNewFolder()
        }, 200)
      }
    } catch (error) {
      console.error('Failed to create folder:', error)
      toast.error(
        error.response?.status === 422
          ? 'A folder with that name already exists.'
          : 'Could not create the folder.'
      )
    }
  }

  const handleFolderEdit = folder => {
    if (!folder) return

    setEditingFolder(folder)
    setEditFolderName(folder.name)
    setEditFolderModalOpen(true)
  }

  const handleEditFolderModalSubmit = async () => {
    if (!editFolderName.trim() || !editingFolder) return
    if (editFolderName.trim() === editingFolder.name) {
      setEditFolderModalOpen(false)
      return
    }

    try {
      setEditFolderModalOpen(false)
      await api.patch(
        `/building-passport/${bp.id}/custom-views/${selectedView.id}/folders/${editingFolder.id}`,
        {
          name: editFolderName.trim()
        }
      )

      // Reload tree structure with force refresh to bypass cache
      await loadTreeStructure(true, true)
    } catch (error) {
      console.error('Failed to edit folder:', error)
      toast.error(
        error.response?.status === 422
          ? 'A folder with that name already exists.'
          : 'Could not rename the folder.'
      )
    }
  }

  const handleFolderDelete = folderId => {
    if (!folderId) return

    setFolderToDelete(folderId)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!folderToDelete) return

    try {
      setDeleteConfirmOpen(false)
      await api.delete(
        `/building-passport/${bp.id}/custom-views/${selectedView.id}/folders/${folderToDelete}`
      )

      // If we're currently viewing the deleted folder, clear selection
      if (selectedNode && selectedNode.id === folderToDelete) {
        setSelectedNode(null)
        if (onFolderSelect) {
          onFolderSelect(null)
        }
      }

      // Invalidate cache for deleted folder and unorganized (files may move there)
      dispatch(invalidateCustomFolderCacheRoutine({
        folderIds: [folderToDelete, null] // null represents 'unorganized'
      }))

      // Reload tree structure with force refresh to bypass cache
      await loadTreeStructure(false, true)
    } catch (error) {
      console.error('Failed to delete folder:', error)
      toast.error('Could not delete the folder.')
    }
  }

  const handleViewCreate = () => {
    setNewViewName('')
    setCreateViewModalOpen(true)
  }

  const submitViewCreate = async () => {
    const name = newViewName.trim()
    if (!name) return

    setViewActionLoading(true)
    try {
      const response = await api.post(
        `/building-passport/${bp.id}/custom-views`,
        {
          name
        }
      )

      setCreateViewModalOpen(false)
      setNewViewName('')
      // Reload custom views and select the new one
      await loadCustomViews()
      if (response.data && response.data.id) {
        setSelectedViewWithCookie(response.data)
      }
      toast.success('View created.')
    } catch (error) {
      console.error('Failed to create view:', error)
      toast.error(
        error.response?.status === 422
          ? 'A view with that name already exists.'
          : 'Could not create the view.'
      )
    } finally {
      setViewActionLoading(false)
    }
  }

  const handleViewEdit = view => {
    if (!view) return
    setViewToEdit(view)
    setEditViewName(view.name || '')
    setEditViewModalOpen(true)
  }

  const submitViewEdit = async () => {
    const name = editViewName.trim()
    if (!viewToEdit) return
    if (!name || name === viewToEdit.name) {
      setEditViewModalOpen(false)
      return
    }

    setViewActionLoading(true)
    try {
      await api.patch(
        `/building-passport/${bp.id}/custom-views/${viewToEdit.id}`,
        {
          name
        }
      )

      setEditViewModalOpen(false)
      setViewToEdit(null)
      // Reload custom views
      await loadCustomViews()
      toast.success('View renamed.')
    } catch (error) {
      console.error('Failed to edit view:', error)
      toast.error(
        error.response?.status === 422
          ? 'A view with that name already exists.'
          : 'Could not rename the view.'
      )
    } finally {
      setViewActionLoading(false)
    }
  }

  const handleViewDelete = viewId => {
    if (!viewId) return
    setViewToDelete(viewId)
    setDeleteViewModalOpen(true)
  }

  const submitViewDelete = async () => {
    if (!viewToDelete) return

    setViewActionLoading(true)
    try {
      await api.delete(
        `/building-passport/${bp.id}/custom-views/${viewToDelete}`
      )

      setDeleteViewModalOpen(false)
      setViewToDelete(null)
      // Reload custom views and select first available one
      await loadCustomViews()
      toast.success('View deleted.')
    } catch (error) {
      console.error('Failed to delete view:', error)
      toast.error('Could not delete the view.')
    } finally {
      setViewActionLoading(false)
    }
  }

  const handleSharePointIntegrate = () => {
    setSharePointModalOpen(true)
  }

  const handleSharePointSuccess = newCustomView => {
    console.log(
      'handleSharePointSuccess: Clearing all state for new SharePoint view',
      newCustomView.id
    )

    // Set switching flag to prevent showing old data
    setIsSwitchingView(true)

    // Clear existing tree data and selected files/folders before switching
    setTreeData(null)
    setUnfilteredTreeData(null)
    setSelectedNode(null)
    setSelectedFiles([])
    dispatch(searchLocalFilesRoutine.success([]))
    setExpandedFolders(new Set())

    // Add the new SharePoint view to the list and select it
    setCustomViews(prev => [...prev, newCustomView])
    setSelectedViewWithCookie(newCustomView)
    setSharePointModalOpen(false)

    // Clear the switching flag after state updates have time to propagate
    setTimeout(() => {
      setIsSwitchingView(false)
    }, 100)
  }

  const handleSharePointAccessEdit = customView => {
    setSelectedSharePointView(customView)
    setSharePointAccessModalOpen(true)
  }

  // Drag selection handlers will be defined after getCurrentItems

  const handleFileDrop = async (file, targetFolderId) => {
    // Check if this file is part of a multi-selection
    const filesToMove =
      selectedFiles.length > 0 && selectedFiles.some(f => f.id === file.id)
        ? selectedFiles
        : [file]

    // Store current state
    const sourceFolderId = selectedNode?.id
    const savedExpandedFolders = new Set(expandedFolders)

    // Find target folder name for toast message
    const findFolderName = (folders, id) => {
      if (!folders) return null
      for (const f of folders) {
        if (f.id === id) return f.name
        if (f.children) {
          const found = findFolderName(f.children, id)
          if (found) return found
        }
      }
      return null
    }
    const targetFolderName = targetFolderId === null || targetFolderId === 'unorganized'
      ? 'Unassigned Files'
      : findFolderName(treeData?.folders, targetFolderId) || 'folder'

    try {
      // Move all files in parallel
      await Promise.all(
        filesToMove.map(f =>
          api.patch(`/building-passport/${bp.id}/files/${f.id}/move-in-custom-view`, {
            customViewId: selectedView.id,
            customFolderId: targetFolderId === 'unorganized' ? null : targetFolderId
          })
        )
      )

      // Show success message
      const fileNames = filesToMove.length === 1
        ? filesToMove[0].name || '1 file'
        : `${filesToMove.length} files`
      toast.success(`Moved ${fileNames} to "${targetFolderName}"`)

      // Clear selection
      setSelectedFiles([])

      // Invalidate all frontend caches
      dispatch(invalidateCustomFolderCacheRoutine({ invalidateAll: true }))
      setEnrichedFolders(new Set())

      // Reload tree structure (backend cache is already invalidated via bumpTreeVersion)
      await loadTreeStructure(false)

      // Restore expanded folders after tree reload
      setExpandedFolders(savedExpandedFolders)

      // Re-select the source folder and reload its files
      setTimeout(() => {
        setTreeData(currentTree => {
          if (!currentTree) return currentTree

          const findFolder = (folders, id) => {
            if (!folders) return null
            for (const f of folders) {
              if (f.id === id) return f
              if (f.children) {
                const found = findFolder(f.children, id)
                if (found) return found
              }
            }
            return null
          }

          let folder = null
          if (sourceFolderId === 'unorganized') {
            folder = currentTree.unorganized
          } else {
            folder = findFolder(currentTree.folders, sourceFolderId)
          }

          if (folder) {
            setSelectedNode(folder)
            setBreadcrumb(buildFolderPath(folder, currentTree))

            // Reload files for source folder
            dispatch(
              loadCustomFolderFilesRoutine({
                bpId: bp.id,
                customViewId: selectedView.id,
                customFolderId: sourceFolderId === 'unorganized' ? null : sourceFolderId
              })
            )

            if (onFolderSelect) {
              onFolderSelect(folder)
            }
          }

          return currentTree
        })
      }, 100)

    } catch (error) {
      console.error('Failed to move files:', error)
      toast.error('Failed to move files. Please try again.')
    }
  }

  // Get file path within custom view structure
  const getFilePathInCustomView = useCallback(
    file => {
      if (!unfilteredTreeData || !file) {
        return null
      }

      // Search through all folders to find where this file belongs
      // Uses fileIds (lightweight ID list from tree structure) — no folder loading needed
      const findFilePath = (folders, fileId, currentPath = []) => {
        for (const folder of folders) {
          // Check fileIds first (from tree structure API), fall back to enriched files array
          const hasFile = folder.fileIds?.includes(fileId) ||
            (folder.files && folder.files.some(f => f.id === fileId))
          if (hasFile) {
            return [...currentPath, folder.name]
          }

          // Check children folders recursively
          if (folder.children && folder.children.length > 0) {
            const childPath = findFilePath(folder.children, fileId, [
              ...currentPath,
              folder.name
            ])
            if (childPath) return childPath
          }
        }
        return null
      }

      // Check if file is in root folders
      if (unfilteredTreeData.folders) {
        const path = findFilePath(unfilteredTreeData.folders, file.id)
        if (path) {
          return path.join(' / ')
        }
      }

      // Check if file is in unorganized (use fileIds or files array)
      const unorg = unfilteredTreeData.unorganized
      if (unorg?.fileIds?.includes(file.id) ||
          unorg?.files?.some(f => f.id === file.id)) {
        return 'Unassigned'
      }

      return null
    },
    [unfilteredTreeData]
  )

  // Build folder path for breadcrumbs
  const buildFolderPath = (currentFolder, treeData) => {
    if (!currentFolder || !treeData) return []

    const path = []
    const findPath = (folders, targetId, currentPath = []) => {
      for (const folder of folders) {
        const newPath = [...currentPath, folder]
        if (folder.id === targetId) {
          return newPath
        }
        if (folder.children) {
          const result = findPath(folder.children, targetId, newPath)
          if (result.length > 0) return result
        }
      }
      return []
    }

    if (currentFolder.id === 'unorganized') {
      return [{ id: 'unorganized', name: 'Unassigned' }]
    }

    const foundPath = findPath(treeData.folders || [], currentFolder.id)
    return foundPath.length > 0 ? foundPath : [currentFolder]
  }

  // Get current items to display in right panel
  const getCurrentItems = useCallback(() => {
    let items = []

    // Get current folder data from enriched treeData (not stale selectedNode)
    let currentNode = selectedNode
    if (selectedNode && selectedNode.type !== 'file' && treeData) {
      if (selectedNode.id === 'unorganized') {
        currentNode = treeData.unorganized || selectedNode
      } else if (selectedNode.id === 'sharepoint-root') {
        // For sharepoint-root, find it in the tree to get enriched data
        const findInTree = (folders, id) => {
          for (const folder of folders) {
            if (folder.id === id) return folder
            if (folder.children) {
              const found = findInTree(folder.children, id)
              if (found) return found
            }
          }
          return null
        }
        const found = findInTree(treeData.folders || [], 'sharepoint-root')
        if (found) {
          currentNode = found
        } else if (treeData.folders && treeData.folders.length > 0) {
          // If not found by ID, use first folder for SharePoint views (it's the root)
          currentNode = treeData.folders[0]
        }
      } else if (selectedNode.id !== 'root') {
        // Find current folder in tree
        const findInTree = (folders, id) => {
          for (const folder of folders) {
            if (folder.id === id) return folder
            if (folder.children) {
              const found = findInTree(folder.children, id)
              if (found) return found
            }
          }
          return null
        }
        const found = findInTree(treeData.folders || [], selectedNode.id)
        if (found) currentNode = found
      }
    }

    // For SharePoint views, only show SharePoint folder structure - no regular files
    if (selectedView?.integrationType === 'sharepoint') {
      // If we're showing search results, narrow by selected folder if any
      if (showingSearchResults) {
        const all = searchResults || []
        let filtered = all

        if (currentNode && currentNode.type !== 'file' && currentNode.id) {
          const collectIds = (folder) => {
            const ids = new Set()
            const walk = (f) => {
              if (!f) return
              const fileIdList = f.fileIds || (f.files || []).map(file => typeof file === 'object' ? file.id : file)
              fileIdList.forEach(id => { if (id) ids.add(id) })
              ;(f.children || []).forEach(walk)
            }
            walk(folder)
            return ids
          }
          const folderFileIds = collectIds(currentNode)
          if (folderFileIds.size > 0) {
            filtered = all.filter(f => folderFileIds.has(f.id))
          }
        }

        return filtered.map(file => ({ ...file, type: 'file' }))
      }

      // If we're in the middle of switching views, show empty to prevent old data flash
      if (isSwitchingView) {
        console.log(
          'Currently switching views, returning empty array to prevent old data flash'
        )
        return []
      }

      // For SharePoint views with no treeData (new views or pending sync), show empty
      if (!treeData || !treeData.folders) {
        console.log('SharePoint view has no treeData, returning empty array')
        return []
      }

      // For SharePoint views with pending sync status, still show folders but indicate loading
      const syncStatus = selectedView?.syncStatus || selectedView?.sync_status
      const isSyncing =
        syncStatus?.status === 'pending' || syncStatus?.status === 'running'

      if (isSyncing) {
        console.log(
          'SharePoint view is syncing, showing folder structure with loading indicator'
        )
        // Don't return empty - let folders show but they might not have files yet
      }

      // Check if current folder is loaded but not yet enriched - show loading state
      if (currentNode && currentNode.type !== 'file') {
        const folderKey =
          currentNode.id === 'unorganized' ? 'unorganized' : currentNode.id
        const isLoaded = loadedCustomFolders.has(folderKey)
        const isEnriched = enrichedFolders.has(folderKey)

        if (isLoaded && !isEnriched) {
          console.log(
            `⏳ Folder ${currentNode.name} (${folderKey}) is loaded but not enriched yet, showing only subfolders`
          )
          // Show subfolders but no files until enriched
          if (currentNode.children && currentNode.children.length > 0) {
            return currentNode.children.map(subfolder => ({
              ...subfolder,
              type: 'folder'
            }))
          }
          return []
        }
      }

      // Handle search results filtering (same as regular custom views)
      if (searchResults && searchResults.length > 0 && !showingSearchResults) {
        const searchResultIds = new Set(searchResults.map(file => file.id))

        if (currentNode && currentNode.type !== 'file') {
          // Add subfolders that contain search results
          if (currentNode.children && currentNode.children.length > 0) {
            const foldersWithResults = currentNode.children
              .filter(child => {
                // Check if this folder or its children have any search results
                const hasResults = folder => {
                  // Check folder's own files (use fileIds from tree API, fall back to enriched files)
                  const ids = folder.fileIds || (folder.files || []).map(f => typeof f === 'object' ? f.id : f)
                  if (ids.some(id => searchResultIds.has(id))) {
                    return true
                  }
                  // Check children recursively
                  if (folder.children) {
                    return folder.children.some(hasResults)
                  }
                  return false
                }
                return hasResults(child)
              })
              .map(subfolder => ({
                ...subfolder,
                type: 'folder'
              }))
            items = [...items, ...foldersWithResults]
          }

          // Add files from search results that belong to current folder
          const currentNodeFileIds = currentNode.fileIds || (currentNode.files || []).map(f => typeof f === 'object' ? f.id : f)
          if (currentNodeFileIds.length > 0) {
            const files = searchResults
              .filter(file => currentNodeFileIds.includes(file.id) && searchResultIds.has(file.id))
              .map(file => ({
                ...file,
                type: 'file'
              }))
            items = [...items, ...files]
          }
        } else if (!selectedNode && treeData) {
          // Show all search results when no specific folder is selected
          items = searchResults.map(file => ({
            ...file,
            type: 'file'
          }))
        }

        // If we have search results but no items to show in current folder,
        // show all search results instead of empty folder
        if (searchResults && searchResults.length > 0 && items.length === 0) {
          items = searchResults.map(file => ({
            ...file,
            type: 'file'
          }))
        }

        return items
      }

      // Only show SharePoint folders if they exist and are from the current view
      if (currentNode && currentNode.type !== 'file') {
        // Add SharePoint subfolders
        if (currentNode.children && currentNode.children.length > 0) {
          const subfolders = currentNode.children.map(subfolder => ({
            ...subfolder,
            type: 'folder'
          }))
          items = [...items, ...subfolders]
        }

        // Add SharePoint files in current folder from Redux
        const folderKey = currentNode.id === 'unorganized' ? 'unorganized' : currentNode.id
        const folderFiles = customFolderFilesMap[folderKey] || []
        if (folderFiles.length > 0) {
          const files = folderFiles.map(file => ({
            ...file,
            type: 'file'
          }))
          items = [...items, ...files]
        }
      } else {
        // Show SharePoint root content when no specific node is selected
        // Add SharePoint root folders
        const sharePointFolders = treeData.folders.map(folder => ({
          ...folder,
          type: 'folder'
        }))
        items = [...items, ...sharePointFolders]

        // Add SharePoint root files from Redux
        const rootFiles = customFolderFilesMap['sharepoint-root'] || []
        if (rootFiles.length > 0) {
          const sharePointRootFiles = rootFiles.map(file => ({
            ...file,
            type: 'file'
          }))
          items = [...items, ...sharePointRootFiles]
        }
      }

      // Return only SharePoint items (no sorting needed as they're already organized)
      return items
    }

    // If we're showing search results, narrow them by the selected folder (if any).
    if (showingSearchResults) {
      const all = searchResults || []
      let filtered = all

      if (currentNode && currentNode.type !== 'file' && currentNode.id) {
        // Collect all file IDs that belong to the selected node (and its descendants)
        const collectFileIds = (folder) => {
          const ids = new Set()
          const walk = (f) => {
            if (!f) return
            // Use fileIds from tree API (lightweight), fall back to enriched files array
            const fileIdList = f.fileIds || (f.files || []).map(file => typeof file === 'object' ? file.id : file)
            fileIdList.forEach(id => { if (id) ids.add(id) })
            ;(f.children || []).forEach(walk)
          }
          walk(folder)
          return ids
        }
        const folderFileIds = collectFileIds(currentNode)
        // Narrow to files in this folder. If folder has no matches, show empty (not all results).
        filtered = all.filter(f => folderFileIds.has(f.id))
      }

      return filtered.map(file => ({ ...file, type: 'file' }))
    }

    // Otherwise show normal folder contents
    if (searchResults && searchResults.length > 0 && !showingSearchResults) {
      const searchResultIds = new Set(searchResults.map(file => file.id))

      if (currentNode && currentNode.type !== 'file') {
        // Add subfolders that contain search results
        if (currentNode.children && currentNode.children.length > 0) {
          const foldersWithResults = currentNode.children
            .filter(child => {
              // Check if this folder or its children have any search results
              const hasResults = folder => {
                // Check folder's own files (use fileIds from tree API, fall back to enriched files)
                const ids = folder.fileIds || (folder.files || []).map(f => typeof f === 'object' ? f.id : f)
                if (ids.some(id => searchResultIds.has(id))) {
                  return true
                }
                // Check children recursively
                if (folder.children) {
                  return folder.children.some(hasResults)
                }
                return false
              }
              return hasResults(child)
            })
            .map(subfolder => ({
              ...subfolder,
              type: 'folder'
            }))
          items = [...items, ...foldersWithResults]
        }

        // Add files from search results that belong to current folder (from Redux)
        const folderKey = currentNode.id === 'unorganized' ? 'unorganized' : currentNode.id
        const folderFiles = customFolderFilesMap[folderKey] || []
        if (folderFiles.length > 0) {
          const files = folderFiles
            .filter(file => searchResultIds.has(file.id))
            .map(file => ({
              ...file,
              type: 'file'
            }))
          items = [...items, ...files]
        }
      } else if (!selectedNode && treeData) {
        // Show all search results when no specific folder is selected
        items = searchResults.map(file => ({
          ...file,
          type: 'file'
        }))
      }

      // If we have search results but no items to show in current folder,
      // show all search results instead of empty folder
      if (searchResults && searchResults.length > 0 && items.length === 0) {
        items = searchResults.map(file => ({
          ...file,
          type: 'file'
        }))
        // Note: We'll set showingSearchResults state outside of this callback
      }
    } else {
      // No search results - show all items normally
      if (currentNode && currentNode.type !== 'file') {
        // Add subfolders
        if (currentNode.children && currentNode.children.length > 0) {
          const subfolders = currentNode.children.map(subfolder => ({
            ...subfolder,
            type: 'folder'
          }))
          items = [...items, ...subfolders]
        }

        // Add files from Redux
        const folderKey = currentNode.id === 'unorganized' ? 'unorganized' : currentNode.id
        const folderFiles = customFolderFilesMap[folderKey] || []
        if (folderFiles.length > 0) {
          const files = folderFiles.map(file => ({
            ...file,
            type: 'file'
          }))
          items = [...items, ...files]
        }
      } else if (!currentNode && treeData) {
        // Show all files from all loaded folders when no specific folder is selected
        const allLoadedFiles = Object.values(customFolderFilesMap).flat()
        items = [...items, ...allLoadedFiles.map(f => ({ ...f, type: 'file' }))]

        // Legacy fallback - if no files loaded yet, check tree
        if (items.length === 0 && treeData.unorganized?.files) {
          items = [
            ...items,
            ...treeData.unorganized.files
              .filter(f => f.displayName || f.display_name || f.name)
              .map(f => ({ ...f, type: 'file' }))
          ]
        }
        // Include root files for SharePoint views (only enriched files)
        if (treeData.rootFiles && treeData.rootFiles.length > 0) {
          items = [
            ...items,
            ...treeData.rootFiles
              .filter(f => f.displayName || f.display_name || f.name)
              .map(f => ({ ...f, type: 'file' }))
          ]
        }
      }
    }

    // Separate folders and files for sorting
    const folders = items.filter(item => item.type === 'folder')
    const files = items
      .filter(item => item.type === 'file')
      // Filter out files that haven't been enriched yet (must have actual file data, not just file_id)
      // Skip this filter during search — search results from the API are already complete
      .filter(file => {
        if (showingSearchResults) return true
        const hasName = !!(file.displayName || file.display_name || file.name)
        const hasFileData = !!(file.url || file.versions?.length > 0 || file.isIdentityRestricted)
        return hasName && hasFileData
      })

    // Sort files but keep folders at the top unsorted
    const sortedFiles = sortFiles(files)

    return [...folders, ...sortedFiles]
  }, [
    selectedNode,
    treeData,
    unfilteredTreeData,
    selectedView,
    refreshKey,
    searchResults,
    showingSearchResults,
    sortBy,
    isSwitchingView,
    customFolderFilesMap,
    loadedCustomFolders,
    enrichedFolders
  ])

  // Toggle file selection for checkbox clicks
  const toggleFileSelection = useCallback(
    (fileId, isChecked, event) => {
      if (event?.shiftKey && selectionAnchorRef.current) {
        const fileItems = getCurrentItems().filter(
          item => item.type === 'file' && item.canBeAccessed !== false
        )
        const rangeFiles = getSelectionRange(
          fileItems,
          selectionAnchorRef.current,
          fileId
        )

        if (rangeFiles.length > 0) {
          setSelectedFiles(rangeFiles)
          return
        }
      }

      selectionAnchorRef.current = fileId
      setSelectedFiles(prev => {
        if (isChecked) {
          // Add file if not already selected
          if (!prev.some(f => f.id === fileId)) {
            const currentItems = getCurrentItems()
            const file = currentItems.find(
              item => item.id === fileId && item.type === 'file'
            )
            return [...prev, file || { id: fileId, type: 'file' }]
          }
          return prev
        } else {
          // Remove file from selection
          return prev.filter(f => f.id !== fileId)
        }
      })
    },
    [getCurrentItems]
  )

  // Drag selection handlers - defined after getCurrentItems
  const handleMouseDown = useCallback(e => {
    // File-card surfaces can start a marquee. Buttons, links, the dedicated
    // file-move handle, and portal content remain interactive.
    if (
      e.target.closest('button') ||
      e.target.closest('[role="button"]') ||
      e.target.closest('a') ||
      e.target.closest('input[type="checkbox"]') ||
      e.target.closest('.MuiCheckbox-root') ||
      e.target.closest('[role="dialog"]') ||
      e.target.closest('.MuiModal-root') ||
      e.target.closest('.MuiDialog-root') ||
      e.target.closest('.MuiBackdrop-root') ||
      e.target.closest('.MuiPopover-root') ||
      e.target.closest('.MuiMenu-root') ||
      e.target.closest('.MuiSelect-root') ||
      e.target.closest('.MuiMenuItem-root') ||
      e.target.closest('.MuiList-root') ||
      e.target.closest('[role="listbox"]') ||
      e.target.closest('[role="option"]') ||
      e.target.closest('[role="presentation"]') ||
      e.target.closest('.MuiPaper-root') ||
      e.target.closest('.enhanced-dropdown') ||
      e.target.closest('#file-preview-portal-root')
    ) {
      return
    }

    if (e.button !== 0) return

    const scrollContainer = virtualizedScrollRef.current
    const wrapper = containerRef.current
    if (!scrollContainer || !wrapper) return

    const rect = scrollContainer.getBoundingClientRect()
    const wrapperRect = wrapper.getBoundingClientRect()
    if (
      e.clientX < rect.left ||
      e.clientX > rect.right ||
      e.clientY < rect.top ||
      e.clientY > rect.bottom
    ) return

    const start = {
      x: e.clientX - rect.left + scrollContainer.scrollLeft,
      y: e.clientY - rect.top + scrollContainer.scrollTop
    }
    const isAdditiveDrag = e.ctrlKey || e.metaKey
    const selectionBase = isAdditiveDrag
      ? selectedFiles.map(file => String(file.id))
      : []
    dragStartRef.current = start
    dragSelectionBaseRef.current = selectionBase
    isAdditiveDragRef.current = isAdditiveDrag
    selectedDuringDragRef.current = new Set(selectionBase)
    dragCardRectsRef.current = new Map()
    lastDragPointerRef.current = { x: e.clientX, y: e.clientY }
    isMouseDownRef.current = true
    setDragStart(start)
    setDragEnd(start)
    setDragScrollOffset({ x: scrollContainer.scrollLeft, y: scrollContainer.scrollTop })
    setDragViewport({
      left: rect.left - wrapperRect.left,
      top: rect.top - wrapperRect.top,
      width: rect.width,
      height: rect.height
    })
    window.__dragMovement = 0
    e.preventDefault()
  }, [selectedFiles])

  const updateDragSelection = useCallback((clientX, clientY) => {
    const scrollContainer = virtualizedScrollRef.current
    const wrapper = containerRef.current
    if (!scrollContainer || !wrapper) return

    const rect = scrollContainer.getBoundingClientRect()
    const wrapperRect = wrapper.getBoundingClientRect()
    const currentX = clientX - rect.left + scrollContainer.scrollLeft
    const currentY = clientY - rect.top + scrollContainer.scrollTop

    setDragEnd({ x: currentX, y: currentY })
    setDragScrollOffset({ x: scrollContainer.scrollLeft, y: scrollContainer.scrollTop })
    setDragViewport({
      left: rect.left - wrapperRect.left,
      top: rect.top - wrapperRect.top,
      width: rect.width,
      height: rect.height
    })

    const visitedFileIds = new Set()
    wrapper.querySelectorAll('.file-card').forEach(card => {
      const fileId = card.dataset.fileId
      if (!fileId || visitedFileIds.has(fileId)) return
      visitedFileIds.add(fileId)

      const cardRect = card.getBoundingClientRect()
      const cardLeft = cardRect.left - rect.left + scrollContainer.scrollLeft
      const cardTop = cardRect.top - rect.top + scrollContainer.scrollTop
      dragCardRectsRef.current.set(fileId, {
        left: cardLeft,
        top: cardTop,
        right: cardLeft + cardRect.width,
        bottom: cardTop + cardRect.height
      })
    })

    const intersectingFileIds = getIntersectingItemIds(
      dragCardRectsRef.current,
      {
        left: dragStartRef.current.x,
        top: dragStartRef.current.y,
        right: currentX,
        bottom: currentY
      }
    )
    const nextSelectedIds = mergeUniqueSelection(
      dragSelectionBaseRef.current,
      intersectingFileIds
    )
    const selectedIds = Array.from(selectedDuringDragRef.current)

    if (!haveSameSelection(selectedIds, nextSelectedIds)) {
      selectedDuringDragRef.current = new Set(nextSelectedIds)
      const itemById = new Map(
        getCurrentItems()
          .filter(item => item.type === 'file')
          .map(item => [String(item.id), item])
      )
      setSelectedFiles(
        nextSelectedIds.map(id => itemById.get(id)).filter(Boolean)
      )
    }
  }, [getCurrentItems])

  const startAutoScroll = useCallback(() => {
    if (autoScrollFrameRef.current) return

    const scrollStep = () => {
      autoScrollFrameRef.current = null
      if (!isDragSelectingRef.current) return

      const scrollContainer = virtualizedScrollRef.current
      if (!scrollContainer) return

      const { x, y } = lastDragPointerRef.current
      const rect = scrollContainer.getBoundingClientRect()
      const scrollSpeed = getAutoScrollSpeed(y - rect.top, rect.height, 120, 48)

      if (scrollSpeed !== 0) {
        const previousScrollTop = scrollContainer.scrollTop
        scrollContainer.scrollBy(0, scrollSpeed)
        if (scrollContainer.scrollTop !== previousScrollTop) {
          updateDragSelection(x, y)
        }
        autoScrollFrameRef.current = window.requestAnimationFrame(scrollStep)
      }
    }

    autoScrollFrameRef.current = window.requestAnimationFrame(scrollStep)
  }, [updateDragSelection])

  const handleMouseMove = useCallback(e => {
    if (!isMouseDownRef.current && !isDragSelectingRef.current) return

    const scrollContainer = virtualizedScrollRef.current
    if (!scrollContainer) return

    lastDragPointerRef.current = { x: e.clientX, y: e.clientY }
    const rect = scrollContainer.getBoundingClientRect()
    const currentX = e.clientX - rect.left + scrollContainer.scrollLeft
    const currentY = e.clientY - rect.top + scrollContainer.scrollTop
    const movement = Math.max(
      Math.abs(currentX - dragStartRef.current.x),
      Math.abs(currentY - dragStartRef.current.y)
    )
    window.__dragMovement = movement

    if (
      !isDragSelectingRef.current &&
      hasExceededDragThreshold(dragStartRef.current, {
        x: currentX,
        y: currentY
      })
    ) {
      isDragSelectingRef.current = true
      selectedDuringDragRef.current = new Set(dragSelectionBaseRef.current)
      dragCardRectsRef.current = new Map()
      if (!isAdditiveDragRef.current) setSelectedFiles([])
      setIsDragSelecting(true)
      window.__isDragSelecting = true
    }

    if (isDragSelectingRef.current) {
      e.preventDefault()
      e.stopPropagation()
      updateDragSelection(e.clientX, e.clientY)
      startAutoScroll()
    }
  }, [startAutoScroll, updateDragSelection])

  const handleVirtualizedScroll = useCallback(() => {
    if (!isDragSelectingRef.current) return
    const { x, y } = lastDragPointerRef.current
    updateDragSelection(x, y)
  }, [updateDragSelection])

  const handleMouseUp = useCallback(e => {
    const completedDragSelection = isDragSelectingRef.current
    if (completedDragSelection) {
      e.preventDefault()
      e.stopPropagation()
      suppressNextContentClickRef.current = true
      window.setTimeout(() => {
        suppressNextContentClickRef.current = false
      }, 0)
    }
    isMouseDownRef.current = false
    isDragSelectingRef.current = false
    dragSelectionBaseRef.current = []
    isAdditiveDragRef.current = false
    dragCardRectsRef.current = new Map()
    if (autoScrollFrameRef.current) {
      window.cancelAnimationFrame(autoScrollFrameRef.current)
      autoScrollFrameRef.current = null
    }
    setIsDragSelecting(false)
    window.__isDragSelecting = false
    window.__dragMovement = 0
  }, [])

  const handleContentClick = useCallback(e => {
    if (suppressNextContentClickRef.current) return
    if (
      e.target.closest('.file-card') ||
      e.target.closest('button') ||
      e.target.closest('input') ||
      e.target.closest('[role="button"]') ||
      e.target.closest('[role="dialog"]') ||
      e.target.closest('.MuiModal-root')
    ) return

    setSelectedFiles([])
    selectionAnchorRef.current = null
  }, [])

  // Event listeners for drag selection (only mousemove and mouseup on document)
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      if (autoScrollFrameRef.current) {
        window.cancelAnimationFrame(autoScrollFrameRef.current)
        autoScrollFrameRef.current = null
      }
      isMouseDownRef.current = false
      isDragSelectingRef.current = false
      dragSelectionBaseRef.current = []
      isAdditiveDragRef.current = false
      dragCardRectsRef.current = new Map()
      window.__isDragSelecting = false
      window.__dragMovement = 0
    }
  }, [handleMouseMove, handleMouseUp])

  // Sorting function (same as FileCategories.js)
  const sortFiles = filesToSort => {
    if (!filesToSort) return []

    // Check if files have search scores - if so, default to relevance sorting
    const hasSearchScores = filesToSort.some(f => f.searchScore !== undefined)

    switch (sortBy) {
      case 'relevance':
        // Sort by search score (highest first)
        return [...filesToSort].sort((a, b) => {
          const scoreA = a.searchScore || 0
          const scoreB = b.searchScore || 0
          return scoreB - scoreA
        })
      case 'newest':
        return [...filesToSort].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        )
      case 'oldest':
        return [...filesToSort].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        )
      case 'name-asc':
        return [...filesToSort].sort((a, b) =>
          a.displayName.localeCompare(b.displayName)
        )
      case 'name-desc':
        return [...filesToSort].sort((a, b) =>
          b.displayName.localeCompare(a.displayName)
        )
      case 'type':
        return [...filesToSort].sort((a, b) => {
          const typeA = getFileTypeName(
            pathOr('', ['fileType', 'id'], a),
            fileTypes,
            a.isPrivate
          )
          const typeB = getFileTypeName(
            pathOr('', ['fileType', 'id'], b),
            fileTypes,
            b.isPrivate
          )
          return typeA.localeCompare(typeB)
        })
      case 'expiry-asc':
        return [...filesToSort].sort((a, b) => {
          if (!a.expirationDate && !b.expirationDate) return 0
          if (!a.expirationDate) return 1
          if (!b.expirationDate) return -1
          return new Date(a.expirationDate) - new Date(b.expirationDate)
        })
      case 'expiry-desc':
        return [...filesToSort].sort((a, b) => {
          if (!a.expirationDate && !b.expirationDate) return 0
          if (!a.expirationDate) return 1
          if (!b.expirationDate) return -1
          return new Date(b.expirationDate) - new Date(a.expirationDate)
        })
      default:
        // If files have search scores and no explicit sort is set, use relevance
        if (hasSearchScores && sortBy === 'newest') {
          return [...filesToSort].sort((a, b) => {
            const scoreA = a.searchScore || 0
            const scoreB = b.searchScore || 0
            return scoreB - scoreA
          })
        }
        return filesToSort
    }
  }

  const getSortLabel = sortBy => {
    switch (sortBy) {
      case 'relevance':
        return 'Relevance'
      case 'newest':
        return 'Newest first'
      case 'oldest':
        return 'Oldest first'
      case 'name-asc':
        return 'Name (A-Z)'
      case 'name-desc':
        return 'Name (Z-A)'
      case 'type':
        return 'File type'
      case 'expiry-asc':
        return 'Expiry date (soonest)'
      case 'expiry-desc':
        return 'Expiry date (latest)'
      default:
        return 'Newest first'
    }
  }

  const openSortMenu = event => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
    if (isMobile) {
      setSortBottomSheetOpen(true)
    } else {
      setSortMenuAnchorEl(event?.currentTarget || event)
    }
  }

  const closeSortMenu = () => {
    setSortMenuAnchorEl(null)
    setSortBottomSheetOpen(false)
  }

  const openActionsMenu = event => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
    if (isMobile) {
      setActionsBottomSheetOpen(true)
    } else {
      setActionsMenuAnchorEl(event?.currentTarget || event)
    }
  }

  const closeActionsMenu = () => {
    setActionsMenuAnchorEl(null)
    setActionsBottomSheetOpen(false)
  }

  const handleClearSelection = () => {
    setSelectedFiles([])
    selectionAnchorRef.current = null
  }

  // Memoize current items to prevent constant re-calculations
  const currentItems = useMemo(() => {
    return getCurrentItems()
  }, [
    selectedNode,
    treeData,
    unfilteredTreeData,
    selectedView,
    refreshKey,
    searchResults,
    showingSearchResults,
    sortBy,
    isSwitchingView,
    customFolderFilesMap,
    loadedCustomFolders,
    enrichedFolders,
    getCurrentItems
  ])

  // Navigate to upload page with custom folder context
  const navigateToUploadPage = () => {
    if (selectedNode?.id === 'unorganized') {
      toast.warning(
        'You cannot upload files directly to the Unassigned folder. Please choose a specific folder to upload the files to.'
      )
      return
    } else if (!selectedNode) {
      toast.warning('Please select a folder to upload the files to.')
      return
    }

    const params = new URLSearchParams({
      method: 'ai',
      customViewId: selectedView?.id,
      customFolderId: selectedNode?.id === 'unorganized' ? '' : selectedNode?.id,
      folderName: selectedNode?.name || 'Unassigned'
    })
    const url = `/files/upload/${bp.id}?${params.toString()}`
    window.open(url, '_blank')
  }

  // No dropzone needed - we use direct navigation to upload page
  // The upload zone now just navigates when clicked

  // Get files list once to avoid filtering on every render
  const filesList = useMemo(
    () => currentItems.filter(item => item.type === 'file'),
    [currentItems]
  )

  useEffect(() => {
    const handleSelectAllShortcut = event => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'a') return

      if (isTextEditingTarget(event.target)) return

      event.preventDefault()
      const selectableFiles = filesList.filter(file => file.canBeAccessed !== false)
      setSelectedFiles(selectableFiles)
      selectionAnchorRef.current = selectableFiles[0]?.id || null
    }

    document.addEventListener('keydown', handleSelectAllShortcut)
    return () => document.removeEventListener('keydown', handleSelectAllShortcut)
  }, [filesList])

  // Memoized data for virtualized lists to prevent constant re-renders
  const gridItemData = useMemo(
    () => ({
      items: filesList,
      selectedFiles,
      toggleFileSelection,
      isDragSelecting,
      getFileTypeName,
      fileTypes,
      searchResults,
      canEdit,
      getCategoryColor,
      handleOpenReclassifyModal,
      getFilePathInCustomView
    }),
    [
      filesList,
      selectedFiles,
      toggleFileSelection,
      isDragSelecting,
      getFileTypeName,
      fileTypes,
      searchResults,
      canEdit,
      getCategoryColor,
      handleOpenReclassifyModal,
      getFilePathInCustomView
    ]
  )

  const listItemData = useMemo(
    () => ({
      items: filesList,
      selectedFiles,
      toggleFileSelection,
      isDragSelecting,
      getFileTypeName,
      fileTypes,
      viewMode,
      searchResults,
      canEdit,
      getCategoryColor,
      handleOpenReclassifyModal,
      getFilePathInCustomView
    }),
    [
      filesList,
      selectedFiles,
      toggleFileSelection,
      isDragSelecting,
      getFileTypeName,
      fileTypes,
      viewMode,
      searchResults,
      canEdit,
      getCategoryColor,
      handleOpenReclassifyModal,
      getFilePathInCustomView
    ]
  )

  // Custom drag layer for mini file card preview
  const CustomDragLayer = () => {
    const { isDragging, item, currentOffset } = useDragLayer(monitor => ({
      item: monitor.getItem(),
      currentOffset: monitor.getSourceClientOffset(),
      isDragging: monitor.isDragging()
    }))

    if (!isDragging || !item || !item.files) {
      return null
    }

    const transform = currentOffset
      ? `translate(${currentOffset.x + 10}px, ${currentOffset.y + 10}px)`
      : null

    const filesToShow = item.files.slice(0, 5) // Show up to 5 files

    return (
      <div
        style={{
          position: 'fixed',
          pointerEvents: 'none',
          zIndex: 100,
          left: 0,
          top: 0,
          transform
        }}
      >
        <DragPreviewContainer>
          {item.files.length > 1 && (
            <FileCountBadge>{item.files.length}</FileCountBadge>
          )}
          {filesToShow.map((file, index) => (
            <MiniFileCard
              key={file.id}
              style={{ zIndex: 10 - index, marginTop: index * -3 }}
            >
              {file.thumbnailUrl ? (
                <MiniThumbnail
                  src={file.thumbnailUrl}
                  alt={file.displayName || file.name}
                />
              ) : (
                <MiniFileIcon>
                  <InsertDriveFileIcon />
                </MiniFileIcon>
              )}
              <MiniFileName>{file.displayName || file.name}</MiniFileName>
            </MiniFileCard>
          ))}
        </DragPreviewContainer>
      </div>
    )
  }

  // Single unified layout - left panel (tree) + right panel (content/preview)
  return (
    <Fragment>
      <Container>
        <Tooltip title='Show folders'>
          <FloatingMenuButton
            type='button'
            style={menuSlot.style}
            onClick={() => setMobileDrawerOpen(true)}
            aria-label='Show folders'
            aria-expanded={mobileDrawerOpen}
          >
            <AccountTreeIcon aria-hidden='true' />
            <FloatingMenuLabel>Folders</FloatingMenuLabel>
          </FloatingMenuButton>
        </Tooltip>

        {typeof document !== 'undefined' &&
          ReactDOM.createPortal(
            <>
              <MobileDrawerOverlay
                isOpen={mobileDrawerOpen}
                onClick={() => setMobileDrawerOpen(false)}
              />

              <MobileDrawer isOpen={mobileDrawerOpen}>
                <MobileDrawerHeader>
                  <MobileDrawerTitle>Custom Views</MobileDrawerTitle>
                  <MobileDrawerClose onClick={() => setMobileDrawerOpen(false)}>
                    <CloseIcon />
                  </MobileDrawerClose>
                </MobileDrawerHeader>
                <MobileDrawerContent>
                  <CustomViewFolders
                    treeData={treeData}
                    selectedNode={selectedNode}
                    onNodeSelect={(node) => {
                      handleNodeSelect(node)
                      setMobileDrawerOpen(false) // Close drawer after selection on mobile
                    }}
                    onFolderCreate={handleFolderCreate}
                    onFolderEdit={handleFolderEdit}
                    onFolderDelete={handleFolderDelete}
                    canEdit={canEdit}
                    loading={loading}
                    initialLoading={initialLoading}
                    customViews={customViews}
                    selectedView={selectedView}
                    onViewSelect={setSelectedViewWithCookie}
                    onViewCreate={handleViewCreate}
                    onViewEdit={handleViewEdit}
                    onViewDelete={handleViewDelete}
                    onSharePointIntegrate={handleSharePointIntegrate}
                    onSharePointAccessEdit={handleSharePointAccessEdit}
                    onFileDrop={handleFileDrop}
                    expandedFolders={expandedFolders}
                    setExpandedFolders={setExpandedFolders}
                    isReadOnly={selectedView?.integrationType === 'sharepoint'}
                    onRefresh={() => loadTreeStructure(false)}
                    onCustomViewRefresh={loadCustomViews}
                    filesLoading={filesLoading}
                    filesLoaded={filesLoaded}
                    loadedCustomFolders={loadedCustomFolders}
                    enrichedFolders={enrichedFolders}
                    loadingCustomFolders={loadingCustomFolders}
                    searchResults={searchResults}
                    isSearching={showingSearchResults}
                  />
                </MobileDrawerContent>
              </MobileDrawer>
            </>,
            document.body
          )}

        {/* Left Panel - Folder Tree */}
        <LeftPanel>
          <CustomViewFolders
            treeData={treeData}
            selectedNode={selectedNode}
            onNodeSelect={handleNodeSelect}
            onFolderCreate={handleFolderCreate}
            onFolderEdit={handleFolderEdit}
            onFolderDelete={handleFolderDelete}
            canEdit={canEdit}
            loading={loading}
            initialLoading={initialLoading}
            customViews={customViews}
            selectedView={selectedView}
            onViewSelect={setSelectedViewWithCookie}
            onViewCreate={handleViewCreate}
            onViewEdit={handleViewEdit}
            onViewDelete={handleViewDelete}
            onSharePointIntegrate={handleSharePointIntegrate}
            onSharePointAccessEdit={handleSharePointAccessEdit}
            onFileDrop={handleFileDrop}
            expandedFolders={expandedFolders}
            setExpandedFolders={setExpandedFolders}
            isReadOnly={selectedView?.integrationType === 'sharepoint'}
            onRefresh={() => {
              // Invalidate all folder caches on manual refresh to ensure fresh data
              dispatch(invalidateCustomFolderCacheRoutine({ invalidateAll: true }))
              loadTreeStructure(false, true)
            }}
            onCustomViewRefresh={loadCustomViews}
            filesLoading={filesLoading}
            filesLoaded={filesLoaded}
            loadedCustomFolders={loadedCustomFolders}
            enrichedFolders={enrichedFolders}
            loadingCustomFolders={loadingCustomFolders}
            searchResults={searchResults}
            isSearching={showingSearchResults}
          />
        </LeftPanel>

        {/* Right Panel - Content */}
        <RightPanel>
          {selectedFile ? (
            /* File Preview Mode - Use actual File component for proper preview */
            <FilePreviewContainer>
              <PreviewHeader>
                <PreviewTitle>
                  {selectedFile.displayName || selectedFile.name}
                </PreviewTitle>
                <PreviewCloseButton onClick={() => setSelectedFile(null)}>
                  ×
                </PreviewCloseButton>
              </PreviewHeader>
              <FilePreviewContent>
                {(() => {
                  const fileUrl = selectedFile.url
                  const fileName = selectedFile.displayName || selectedFile.name

                  // Image files - show direct image
                  if (/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(fileUrl)) {
                    return (
                      <PreviewImage
                        src={fileUrl}
                        alt={fileName}
                        onError={e => {
                          e.target.style.display = 'none'
                          e.target.nextSibling.style.display = 'block'
                        }}
                      />
                    )
                  }

                  // PDF files - show embedded PDF
                  if (/\.pdf$/i.test(fileUrl)) {
                    return (
                      <div style={{ width: '100%', height: '100%' }}>
                        <PDFDocument fileUrl={fileUrl} fileName={fileName} />
                      </div>
                    )
                  }

                  // Other files - show File component for download/actions
                  return (
                    <PreviewPlaceholder>
                      <File
                        file={selectedFile}
                        type={getFileTypeName(
                          pathOr('', ['fileType', 'id'], selectedFile),
                          fileTypes,
                          selectedFile.isPrivate
                        )}
                        viewType='grid'
                        viewMode='grid'
                        hideBulkSelect={true}
                        showThumbnail={true}
                        isSharedWithUnits={selectedFile.isSharedWithUnits}
                        customPath={getFilePathInCustomView(selectedFile)}
                        isInSearchBar={false}
                        canEdit={canEdit}
                        categoryColor={getCategoryColor(selectedFile)}
                        onOpenReclassifyModal={() =>
                          handleOpenReclassifyModal(selectedFile)
                        }
                        onOpenEditModal={handleEditModalOpen}
                        onOpenPermissionsModal={setPermissionsFile}
                        onOpenViewedStatusModal={setViewedStatusFile}
                        externalModalControl={true}
                        onSelect={toggleFileSelection}
                        disableSelectionHighlight={true}
                        searchQuery={searchQuery}
                        searchMode={searchMode}
                        forceMobileMenu={isTouchDevice}
                      />
                    </PreviewPlaceholder>
                  )
                })()}
              </FilePreviewContent>
            </FilePreviewContainer>
          ) : (
            /* File Grid View */
            <FileGridContainer>
              {/* Search Results Indicator */}
              {showingSearchResults && (
                <SearchResultsIndicator>
                  <Typography
                    variant='body2'
                    style={{
                      color: searchLoading ? '#1a8a96' : searchResults?.length > 0 ? '#4caf50' : '#d24724',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    {searchLoading && <CircularProgress size={14} style={{ color: '#1a8a96' }} />}
                    {!searchLoading && searchResults?.length > 0 && <span style={{ fontSize: 16 }}>&#10003;</span>}
                    {searchLoading
                      ? 'Searching files...'
                      : searchResults?.length > 0
                        ? `${searchResults.length} files found across all folders`
                        : 'No results found'}
                  </Typography>
                  {!searchLoading && isSearchTruncated && (
                    <Typography
                      variant='caption'
                      style={{ color: '#666', marginLeft: 8 }}
                    >
                      • Showing the first 300 results.{' '}
                      <span
                        onClick={handleLoadAllResults}
                        style={{ color: '#33becc', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        Load all results
                      </span>
                    </Typography>
                  )}
                  {!searchLoading && !isSearchTruncated && searchResults?.length > 0 && (
                    <Typography
                      variant='caption'
                      style={{ color: '#666', marginLeft: 8 }}
                    >
                      • Search complete
                    </Typography>
                  )}
                </SearchResultsIndicator>
              )}

              {/* Breadcrumb Navigation - Always visible */}
              {!showingSearchResults && (
                <BreadcrumbContainer>
                  <BreadcrumbList>
                    {breadcrumb.map((folder, index) => (
                      <Fragment key={folder.id}>
                        {index > 0 && (
                          <BreadcrumbSeparator>
                            <ChevronRightIcon />
                          </BreadcrumbSeparator>
                        )}
                        <BreadcrumbItem
                          onClick={e => {
                            // Don't allow clicking on Unassigned
                            if (folder.id === 'unorganized') {
                              return
                            }
                            console.log(
                              'Breadcrumb folder clicked',
                              folder.name,
                              'index:',
                              index,
                              'breadcrumb length:',
                              breadcrumb.length,
                              e
                            )
                            e.stopPropagation()
                            // Allow clicking all breadcrumb items for navigation
                            handleNodeSelect(folder)
                          }}
                          isClickable={folder.id !== 'unorganized'}
                          isActive={index === breadcrumb.length - 1}
                        >
                          {folder.name}
                        </BreadcrumbItem>
                      </Fragment>
                    ))}
                    {selectedView?.integrationType === 'sharepoint' ? (
                      <>
                        <BreadcrumbSeparator />
                        <span
                          style={{
                            fontSize: '12px',
                            color: '#666',
                            fontStyle: 'italic',
                            marginLeft: '8px'
                          }}
                        >
                          Read-only view
                        </span>
                      </>
                    ) : (
                      <>
                        {canEdit && !selectedView?.is_read_only && (
                          <BreadcrumbSeparator>
                            <AddIcon />
                          </BreadcrumbSeparator>
                        )}
                        {canEdit && !selectedView?.is_read_only && (
                          <Tooltip title='Add subfolder here'>
                            <AddFolderButton
                              onClick={e => {
                                console.log(
                                  'Add Folder button clicked',
                                  selectedNode?.id,
                                  e
                                )
                                e.stopPropagation()
                                handleFolderCreate(selectedNode?.id || null)
                              }}
                            >
                              Add Folder
                            </AddFolderButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </BreadcrumbList>
                </BreadcrumbContainer>
              )}

              {/* View Toggle */}
              <ViewToggle>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title='Grid view'>
                    <StyledIconButton
                      isActive={viewMode === 'grid'}
                      onClick={() => setViewMode('grid')}
                    >
                      <GridViewIcon />
                    </StyledIconButton>
                  </Tooltip>
                  <Tooltip title='List view'>
                    <StyledIconButton
                      isActive={viewMode === 'list'}
                      onClick={() => setViewMode('list')}
                    >
                      <ListIcon />
                    </StyledIconButton>
                  </Tooltip>

                  {selectedFiles.length > 0 && (
                    <>
                      <SelectedFilesCount>
                        {selectedFiles.length}
                        <SelectedText> selected</SelectedText>
                      </SelectedFilesCount>
                      <Tooltip title='Deselect all'>
                        <SelectAllButton onClick={handleClearSelection}>
                          <SelectAllText>Deselect all</SelectAllText>
                        </SelectAllButton>
                      </Tooltip>
                    </>
                  )}
                  {selectedFiles.length === 0 && (
                    <Tooltip title='Select all'>
                      <SelectAllButton
                        onClick={() => {
                          const visibleFiles = filesList.filter(f => f.canBeAccessed !== false)
                          setSelectedFiles(visibleFiles)
                          selectionAnchorRef.current = visibleFiles[0]?.id || null
                        }}
                      >
                        <SelectAllText>Select all</SelectAllText>
                      </SelectAllButton>
                    </Tooltip>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {selectedFiles.length === 0 && (
                    <SortButton onClick={openSortMenu}>
                      <SortIcon fontSize='small' />
                      <SortText>Sort: {getSortLabel(sortBy)}</SortText>
                    </SortButton>
                  )}

                  {selectedFiles.length > 0 && (
                    <Tooltip title='More actions'>
                      <ActionIconButton onClick={openActionsMenu}>
                        <MoreVertIcon fontSize='small' />
                      </ActionIconButton>
                    </Tooltip>
                  )}

                  {typeof window !== 'undefined' && window.innerWidth > 768 && (
                    <Menu
                      anchorEl={actionsMenuAnchorEl}
                      open={Boolean(actionsMenuAnchorEl)}
                      onClose={closeActionsMenu}
                    >
                      <MenuItem disabled>
                        <strong>{selectedFiles.length} selected</strong>
                      </MenuItem>
                      <MenuItem onClick={handleClearSelection}>
                        Clear selection
                      </MenuItem>
                      <MenuItem divider />
                      {canEdit && (
                        <>
                          <MenuItem onClick={() => {
                            setBulkEditModalOpen(true)
                            closeActionsMenu()
                          }}>
                            <EditIcon style={{ marginRight: 8, fontSize: 18 }} />
                            Edit
                          </MenuItem>
                          <MenuItem onClick={() => {
                            setBulkShareModalOpen(true)
                            closeActionsMenu()
                          }}>
                            <ShareIcon style={{ marginRight: 8, fontSize: 18 }} />
                            Share
                          </MenuItem>
                          <MenuItem onClick={() => {
                            setBulkUnshareModalOpen(true)
                            closeActionsMenu()
                          }}>
                            <LinkOffIcon style={{ marginRight: 8, fontSize: 18 }} />
                            Unshare
                          </MenuItem>
                          <MenuItem divider />
                        </>
                      )}
                      <MenuItem onClick={() => {
                        closeActionsMenu()
                        openSortMenu({ currentTarget: actionsMenuAnchorEl })
                      }}>
                        <SortIcon style={{ marginRight: 8, fontSize: 18 }} />
                        Sort: {getSortLabel(sortBy)}
                      </MenuItem>
                    </Menu>
                  )}
                </div>
              </ViewToggle>

              <Menu
                anchorEl={sortMenuAnchorEl}
                open={Boolean(sortMenuAnchorEl)}
                onClose={closeSortMenu}
              >
                {Array.from(filesMap.values()).some(f => f.searchScore !== undefined) && (
                  <MenuItem
                    selected={sortBy === 'relevance'}
                    onClick={() => {
                      setSortBy('relevance')
                      closeSortMenu()
                    }}
                  >
                    Relevance
                  </MenuItem>
                )}
                <MenuItem
                  selected={sortBy === 'newest'}
                  onClick={() => {
                    setSortBy('newest')
                    closeSortMenu()
                  }}
                >
                  Newest first
                </MenuItem>
                <MenuItem
                  selected={sortBy === 'oldest'}
                  onClick={() => {
                    setSortBy('oldest')
                    closeSortMenu()
                  }}
                >
                  Oldest first
                </MenuItem>
                <MenuItem
                  selected={sortBy === 'name-asc'}
                  onClick={() => {
                    setSortBy('name-asc')
                    closeSortMenu()
                  }}
                >
                  Name (A-Z)
                </MenuItem>
                <MenuItem
                  selected={sortBy === 'name-desc'}
                  onClick={() => {
                    setSortBy('name-desc')
                    closeSortMenu()
                  }}
                >
                  Name (Z-A)
                </MenuItem>
                <MenuItem
                  selected={sortBy === 'type'}
                  onClick={() => {
                    setSortBy('type')
                    closeSortMenu()
                  }}
                >
                  File type
                </MenuItem>
                <MenuItem
                  selected={sortBy === 'expiry-asc'}
                  onClick={() => {
                    setSortBy('expiry-asc')
                    closeSortMenu()
                  }}
                >
                  Expiry date (soonest)
                </MenuItem>
                <MenuItem
                  selected={sortBy === 'expiry-desc'}
                  onClick={() => {
                    setSortBy('expiry-desc')
                    closeSortMenu()
                  }}
                >
                  Expiry date (latest)
                </MenuItem>
              </Menu>

              {typeof window !== 'undefined' && window.innerWidth <= 768 && ReactDOM.createPortal(
                <>
                  {sortBottomSheetOpen && (
                    <>
                      <SortBottomSheetOverlay onClick={closeSortMenu} />
                      <SortBottomSheet>
                        <SortBottomSheetHandle />
                        <SortBottomSheetHeader>
                          <SortBottomSheetTitle>Sort files</SortBottomSheetTitle>
                          <SortBottomSheetCloseButton onClick={closeSortMenu}>
                            ×
                          </SortBottomSheetCloseButton>
                        </SortBottomSheetHeader>
                        <SortBottomSheetContent>
                          {Array.from(filesMap.values()).some(f => f.searchScore !== undefined) && (
                            <SortBottomSheetItem
                              selected={sortBy === 'relevance'}
                              onClick={() => {
                                setSortBy('relevance')
                                closeSortMenu()
                              }}
                            >
                              <SortBottomSheetText>Relevance</SortBottomSheetText>
                            </SortBottomSheetItem>
                          )}
                          <SortBottomSheetItem
                            selected={sortBy === 'newest'}
                            onClick={() => {
                              setSortBy('newest')
                              closeSortMenu()
                            }}
                          >
                            <SortBottomSheetText>Newest first</SortBottomSheetText>
                          </SortBottomSheetItem>
                          <SortBottomSheetItem
                            selected={sortBy === 'oldest'}
                            onClick={() => {
                              setSortBy('oldest')
                              closeSortMenu()
                            }}
                          >
                            <SortBottomSheetText>Oldest first</SortBottomSheetText>
                          </SortBottomSheetItem>
                          <SortBottomSheetItem
                            selected={sortBy === 'name-asc'}
                            onClick={() => {
                              setSortBy('name-asc')
                              closeSortMenu()
                            }}
                          >
                            <SortBottomSheetText>Name (A-Z)</SortBottomSheetText>
                          </SortBottomSheetItem>
                          <SortBottomSheetItem
                            selected={sortBy === 'name-desc'}
                            onClick={() => {
                              setSortBy('name-desc')
                              closeSortMenu()
                            }}
                          >
                            <SortBottomSheetText>Name (Z-A)</SortBottomSheetText>
                          </SortBottomSheetItem>
                          <SortBottomSheetItem
                            selected={sortBy === 'type'}
                            onClick={() => {
                              setSortBy('type')
                              closeSortMenu()
                            }}
                          >
                            <SortBottomSheetText>File type</SortBottomSheetText>
                          </SortBottomSheetItem>
                          <SortBottomSheetItem
                            selected={sortBy === 'expiry-asc'}
                            onClick={() => {
                              setSortBy('expiry-asc')
                              closeSortMenu()
                            }}
                          >
                            <SortBottomSheetText>Expiry date (soonest)</SortBottomSheetText>
                          </SortBottomSheetItem>
                          <SortBottomSheetItem
                            selected={sortBy === 'expiry-desc'}
                            onClick={() => {
                              setSortBy('expiry-desc')
                              closeSortMenu()
                            }}
                          >
                            <SortBottomSheetText>Expiry date (latest)</SortBottomSheetText>
                          </SortBottomSheetItem>
                        </SortBottomSheetContent>
                      </SortBottomSheet>
                    </>
                  )}

                  {/* Bottom Sheet for Actions Menu on Mobile */}
                  {actionsBottomSheetOpen && (
                    <>
                      <SortBottomSheetOverlay onClick={closeActionsMenu} />
                      <SortBottomSheet>
                        <SortBottomSheetHandle />
                        <SortBottomSheetHeader>
                          <SortBottomSheetTitle>Actions</SortBottomSheetTitle>
                          <SortBottomSheetCloseButton onClick={closeActionsMenu}>
                            ×
                          </SortBottomSheetCloseButton>
                        </SortBottomSheetHeader>
                        <SortBottomSheetContent>
                          <SortBottomSheetItem style={{ opacity: 0.6 }} disabled>
                            <SortBottomSheetText style={{ fontWeight: 600 }}>
                              {selectedFiles.length} selected
                            </SortBottomSheetText>
                          </SortBottomSheetItem>
                          <SortBottomSheetItem
                            onClick={() => {
                              handleClearSelection()
                              closeActionsMenu()
                            }}
                          >
                            <SortBottomSheetText>Clear selection</SortBottomSheetText>
                          </SortBottomSheetItem>
                          {canEdit && (
                            <>
                              <SortBottomSheetItem
                                onClick={() => {
                                  setBulkEditModalOpen(true)
                                  closeActionsMenu()
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                  <EditIcon style={{ fontSize: 20, color: '#1f2937' }} />
                                  <SortBottomSheetText>Edit</SortBottomSheetText>
                                </div>
                              </SortBottomSheetItem>
                              <SortBottomSheetItem
                                onClick={() => {
                                  setBulkShareModalOpen(true)
                                  closeActionsMenu()
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                  <ShareIcon style={{ fontSize: 20, color: '#1f2937' }} />
                                  <SortBottomSheetText>Share</SortBottomSheetText>
                                </div>
                              </SortBottomSheetItem>
                              <SortBottomSheetItem
                                onClick={() => {
                                  setBulkUnshareModalOpen(true)
                                  closeActionsMenu()
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                  <LinkOffIcon style={{ fontSize: 20, color: '#1f2937' }} />
                                  <SortBottomSheetText>Unshare</SortBottomSheetText>
                                </div>
                              </SortBottomSheetItem>
                            </>
                          )}
                          <SortBottomSheetItem
                            onClick={() => {
                              closeActionsMenu()
                              setSortBottomSheetOpen(true)
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <SortIcon style={{ fontSize: 20, color: '#1f2937' }} />
                              <SortBottomSheetText>Sort: {getSortLabel(sortBy)}</SortBottomSheetText>
                            </div>
                          </SortBottomSheetItem>
                        </SortBottomSheetContent>
                      </SortBottomSheet>
                    </>
                  )}
                </>,
                document.body
              )}

              <ContentArea
                ref={containerRef}
                style={{ position: 'relative', userSelect: 'none' }}
                onMouseDown={handleMouseDown}
                onClick={handleContentClick}
                className={isDragSelecting ? 'drag-selecting' : ''}
              >
                {/* Drag selection overlay */}
                {isDragSelecting && (
                  <SelectionOverlay
                    style={{
                      left: Math.max(
                        dragViewport.left,
                        dragViewport.left + Math.min(dragStart.x, dragEnd.x) - dragScrollOffset.x
                      ),
                      top: Math.max(
                        dragViewport.top,
                        dragViewport.top + Math.min(dragStart.y, dragEnd.y) - dragScrollOffset.y
                      ),
                      width: Math.max(
                        0,
                        Math.min(
                          dragViewport.left + dragViewport.width,
                          dragViewport.left + Math.max(dragStart.x, dragEnd.x) - dragScrollOffset.x
                        ) - Math.max(
                          dragViewport.left,
                          dragViewport.left + Math.min(dragStart.x, dragEnd.x) - dragScrollOffset.x
                        )
                      ),
                      height: Math.max(
                        0,
                        Math.min(
                          dragViewport.top + dragViewport.height,
                          dragViewport.top + Math.max(dragStart.y, dragEnd.y) - dragScrollOffset.y
                        ) - Math.max(
                          dragViewport.top,
                          dragViewport.top + Math.min(dragStart.y, dragEnd.y) - dragScrollOffset.y
                        )
                      )
                    }}
                  >
                    <DragSelectionHint>
                      Hold CMD or CTRL to add to the selection
                    </DragSelectionHint>
                  </SelectionOverlay>
                )}
                {initialLoading || loading ? (
                  <SkeletonGrid>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <SkeletonCard key={i}>
                        <Skeleton variant='rectangular' width='100%' height={180} sx={{ borderRadius: 1 }} />
                        <Skeleton variant='text' width='70%' height={20} sx={{ mt: 1 }} />
                        <Skeleton variant='text' width='40%' height={16} />
                      </SkeletonCard>
                    ))}
                  </SkeletonGrid>
                ) : searchLoading ? (
                  <SearchLoadingContainer>
                    <SearchLoadingSpinner size={18} />
                    <SearchLoadingText>Searching files...</SearchLoadingText>
                  </SearchLoadingContainer>
                ) : (
                  (() => {
                    const folders = currentItems.filter(
                      item => item.type === 'folder'
                    )
                    const files = currentItems.filter(
                      item => item.type === 'file'
                    )

                    // SIMPLE RULE: Show loading if the number of files in tree doesn't match displayed files
                    // Get the CURRENT folder data from treeData (not stale selectedNode)
                    let currentFolderData = selectedNode
                    if (selectedNode && treeData) {
                      if (selectedNode?.id === 'unorganized') {
                        currentFolderData = treeData.unorganized || selectedNode
                      } else {
                        // Find current folder in tree
                        const findInTree = (folders, id) => {
                          for (const folder of folders) {
                            if (folder.id === id) return folder
                            if (folder.children) {
                              const found = findInTree(folder.children, id)
                              if (found) return found
                            }
                          }
                          return null
                        }
                        currentFolderData =
                          findInTree(treeData.folders || [], selectedNode.id) ||
                          selectedNode
                      }
                    }

                    const treeFileCount = currentFolderData?.files?.length || 0
                    const displayedFileCount = files.length

                    // Check if we're actually loading files for this specific folder
                    const isActuallyLoading = selectedNode
                      ? (selectedNode.id === 'unorganized' &&
                          loadingCustomFolders.has('unorganized')) ||
                        (selectedNode.id.match(
                          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
                        ) &&
                          loadingCustomFolders.has(selectedNode.id))
                      : false

                    // Check if this specific folder has been loaded/enriched
                    const folderKey = selectedNode
                      ? selectedNode.id === 'unorganized'
                        ? 'unorganized'
                        : selectedNode.id
                      : null
                    const isFolderEnriched = enrichedFolders.has(folderKey)

                    // Show loading ONLY if:
                    // 1. Files count doesn't match (files in tree but not displayed)
                    // 2. This specific folder is actively loading (not just allFilesLoaded)
                    // 3. This folder hasn't been enriched yet
                    // 4. Search is NOT active — when searching, we don't need to load all
                    //    folder files, the search results already give us what we need.
                    if (
                      !showingSearchResults &&
                      treeFileCount !== displayedFileCount &&
                      isActuallyLoading &&
                      !isFolderEnriched
                    ) {
                      // Show loading indicator with progress bar
                      return (
                        <FileLoadingProgress
                          loadingKey={folderKey}
                          totalFiles={treeFileCount}
                        />
                      )
                    }

                    if (currentItems.length === 0) {
                      return (
                        <>
                          {/* Upload Drop Zone for empty folders - hide during search */}
                          {!showingSearchResults &&
                            canEdit &&
                            !selectedView?.is_read_only &&
                            selectedView?.integrationType !== 'sharepoint' && (
                              <>
                                {treeData?.folders?.length > 0 ? (
                                  <CompactUploadZone
                                    onClick={navigateToUploadPage}
                                    isActive={false}
                                    canEdit={canEdit}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <UploadIcon sx={{ fontSize: 18 }} />
                                    <span>
                                      {`Add files to "${folderLabel}" – click here to upload`}
                                    </span>
                                  </CompactUploadZone>
                                ) : (
                                  <NoFoldersMessage>
                                    Set up your folders before uploading any
                                    files
                                  </NoFoldersMessage>
                                )}
                              </>
                            )}

                          <EmptyState>
                            <Typography variant='h6' color='textPrimary'>
                              Empty
                            </Typography>
                            <Typography variant='body2' color='textSecondary'>
                              {selectedNode && selectedNode.type !== 'file'
                                ? 'This folder is empty'
                                : 'No files in this view'}
                            </Typography>
                          </EmptyState>
                        </>
                      )
                    }

                    return (
                      <>
                        {/* Upload Zone - Compact version at top - hide during search */}
                        {!showingSearchResults &&
                          canEdit &&
                          selectedView?.integrationType !== 'sharepoint' && (
                            <CompactUploadZone
                              onClick={navigateToUploadPage}
                              isActive={false}
                              canEdit={canEdit}
                              style={{ cursor: 'pointer' }}
                            >
                              <UploadIcon sx={{ fontSize: 18 }} />
                              <span>
                                {`Add files to "${folderLabel}" – click here to upload`}
                              </span>
                            </CompactUploadZone>
                          )}
                        {false && hasPendingUpload && (
                          <ResumeUploadButton
                            onClick={() => {}}
                          >
                            Go to Uploads
                          </ResumeUploadButton>
                        )}

                        {/* Folders Section */}
                        {folders.length > 0 && (
                          <FoldersSection>
                            {folders.map(folder => (
                              <FolderCard
                                key={folder.id}
                                onClick={() => handleNodeSelect(folder)}
                              >
                                <FolderIcon
                                  sx={{ fontSize: 24, color: '#33becc', mr: 1 }}
                                />
                                <FolderInfo>
                                  <FolderName>{folder.name}</FolderName>
                                  <FolderCount>
                                    {folder.fileCount || 0} files
                                  </FolderCount>
                                </FolderInfo>
                              </FolderCard>
                            ))}
                          </FoldersSection>
                        )}

                        {/* Files Section */}
                        {files.length > 0 && (
                          <FilesSection viewMode={viewMode}>
                            {viewMode === 'grid' ? (
                              <AutoSizer>
                                {({ height, width }) => {
                                  let columnCount
                                  let itemWidth
                                  let itemHeight

                                  if (width <= 500) {
                                    columnCount = 1
                                    itemWidth = width - MOBILE_SCROLL_GUTTER
                                    itemHeight = 320
                                  } else if (width <= 768) {
                                    columnCount = 2
                                    itemWidth = Math.floor((width - MOBILE_SCROLL_GUTTER) / 2)
                                    itemHeight = 320
                                  } else {
                                    columnCount = Math.floor(width / GRID_ITEM_WIDTH) || 1
                                    itemWidth = GRID_ITEM_WIDTH
                                    itemHeight = GRID_ITEM_HEIGHT
                                  }

                                  const rowCount = Math.ceil(files.length / columnCount)
                                  const isMobile = width <= 768 || isTouchDevice

                                  return (
                                    <FixedSizeGrid
                                      outerRef={virtualizedScrollRef}
                                      style={{ overflowX: 'hidden' }}
                                      height={height || 1000}
                                      width={width}
                                      columnCount={columnCount}
                                      rowCount={rowCount}
                                      columnWidth={itemWidth}
                                      rowHeight={itemHeight}
                                      onScroll={handleVirtualizedScroll}
                                      itemData={{
                                        ...gridItemData,
                                        columnCount,
                                        isMobile,
                                        handleEditModalOpen,
                                        setPermissionsFile,
                                        setViewedStatusFile,
                                        searchQuery,
                                        searchMode,
                                        isTouchDevice
                                      }}
                                    >
                                      {FileGridItem}
                                    </FixedSizeGrid>
                                  )
                                }}
                              </AutoSizer>
                            ) : (
                              <ListScroller
                                ref={virtualizedScrollRef}
                                onScroll={handleVirtualizedScroll}
                              >
                                {files.map((file) => (
                                  <div
                                    key={file.id}
                                    style={{
                                      marginBottom: '8px',
                                      width: '100%'
                                    }}
                                  >
                                    <div className='file-card' data-file-id={file.id}>
                                      <DraggableFile file={file} selectedFiles={selectedFiles} canEdit={canEdit} viewMode={viewMode}>
                                        <File
                                          file={file}
                                          type={getFileTypeName(
                                            pathOr('', ['fileType', 'id'], file),
                                            fileTypes,
                                            file.isPrivate
                                          )}
                                          viewType='list'
                                          viewMode='list'
                                          hideBulkSelect={false}
                                          showThumbnail={false}
                                          isSharedWithUnits={file.isSharedWithUnits}
                                          customPath={getFilePathInCustomView(file)}
                                          isInSearchBar={!!searchResults}
                                          canEdit={canEdit}
                                          categoryColor={getCategoryColor(file)}
                                          onOpenReclassifyModal={() => handleOpenReclassifyModal(file)}
                                          onOpenEditModal={handleEditModalOpen}
                                          onOpenPermissionsModal={setPermissionsFile}
                                          onOpenViewedStatusModal={setViewedStatusFile}
                                          externalModalControl={true}
                                          isSelected={selectedFiles.some(sf => sf.id === file.id)}
                                          onSelect={toggleFileSelection}
                                          isDragSelecting={isDragSelecting}
                                          disableSelectionHighlight={true}
                                          searchQuery={searchQuery}
                                          searchMode={searchMode}
                                          forceMobileMenu={isTouchDevice}
                                        />
                                      </DraggableFile>
                                    </div>
                                  </div>
                                ))}
                                <div style={{ height: '40px' }} />
                              </ListScroller>
                            )}
                          </FilesSection>
                        )}
                      </>
                    )
                  })()
                )}
              </ContentArea>
            </FileGridContainer>
          )}
        </RightPanel>
      </Container>

      {/* Folder Creation Modal */}
      <Dialog open={folderModalOpen} onClose={() => setFolderModalOpen(false)}>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='Folder Name'
            fullWidth
            variant='outlined'
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                handleFolderModalSubmit()
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleFolderModalSubmit}
            color='primary'
            variant='contained'
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Folder Modal */}
      <Dialog
        open={editFolderModalOpen}
        onClose={() => setEditFolderModalOpen(false)}
      >
        <DialogTitle>Edit Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='Folder Name'
            fullWidth
            variant='outlined'
            value={editFolderName}
            onChange={e => setEditFolderName(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                handleEditFolderModalSubmit()
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFolderModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditFolderModalSubmit}
            color='primary'
            variant='contained'
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Folder Confirmation Modal */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete Folder</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this folder? All files in it will be
            moved to unassigned.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color='error'
            variant='contained'
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create View Modal */}
      <Dialog
        open={createViewModalOpen}
        onClose={() => setCreateViewModalOpen(false)}
      >
        <DialogTitle>Create custom view</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='View name'
            fullWidth
            variant='outlined'
            value={newViewName}
            onChange={e => setNewViewName(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                submitViewCreate()
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateViewModalOpen(false)}>Cancel</Button>
          <Button
            onClick={submitViewCreate}
            color='primary'
            variant='contained'
            disabled={!newViewName.trim() || viewActionLoading}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename View Modal */}
      <Dialog
        open={editViewModalOpen}
        onClose={() => setEditViewModalOpen(false)}
      >
        <DialogTitle>Rename view</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='View name'
            fullWidth
            variant='outlined'
            value={editViewName}
            onChange={e => setEditViewName(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                submitViewEdit()
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditViewModalOpen(false)}>Cancel</Button>
          <Button
            onClick={submitViewEdit}
            color='primary'
            variant='contained'
            disabled={!editViewName.trim() || viewActionLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete View Confirmation Modal */}
      <Dialog
        open={deleteViewModalOpen}
        onClose={() => setDeleteViewModalOpen(false)}
      >
        <DialogTitle>Delete view</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this view? All its folders and file
            assignments will be permanently deleted. Your files themselves are
            not deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteViewModalOpen(false)}>Cancel</Button>
          <Button
            onClick={submitViewDelete}
            color='error'
            variant='contained'
            disabled={viewActionLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Reclassify Modal - managed at this level to prevent unmount issues */}
      <ReclassifyFileModal
        open={reclassifyModalState.open}
        onClose={handleCloseReclassifyModal}
        file={reclassifyModalState.file}
        onOpenEditModal={file => handleEditModalOpen(file)}
      />
      <EditFileModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        file={editingFile}
        bp={bp}
      />
      <FilePermissionsModal
        open={Boolean(permissionsFile)}
        onClose={() => setPermissionsFile(null)}
        file={permissionsFile}
      />
      {viewedStatusFile && (
        <ViewedStatusModal
          file={viewedStatusFile}
          canSeeHistory={viewedStatusFile?.buildingPassport?.isOwner}
          externalOpen
          onExternalClose={() => setViewedStatusFile(null)}
        />
      )}

      {/* SharePoint Integration Modal */}
      <SharePointIntegrationModal
        open={sharePointModalOpen}
        onClose={() => setSharePointModalOpen(false)}
        buildingPassportId={bp?.id}
        onSuccess={handleSharePointSuccess}
      />

      {/* SharePoint Access Control Modal */}
      <SharePointAccessControlModal
        open={sharePointAccessModalOpen}
        onClose={() => setSharePointAccessModalOpen(false)}
        customView={selectedSharePointView}
        buildingPassportId={bp?.id}
        onSuccess={updatedCustomView => {
          setSharePointAccessModalOpen(false)
          setSelectedSharePointView(null)
          loadCustomViews()
        }}
        onReauthRequired={(customViewId) => {
          setSharePointReauthCustomViewId(customViewId)
          setSharePointReauthModalOpen(true)
        }}
      />

      {/* SharePoint Re-auth Modal (triggered after sync mode change) */}
      <SharePointReauthModal
        open={sharePointReauthModalOpen}
        onClose={() => {
          setSharePointReauthModalOpen(false)
          setSharePointReauthCustomViewId(null)
        }}
        customViewId={sharePointReauthCustomViewId}
        buildingPassportId={bp?.id}
        bp={bp}
        onSuccess={() => {
          setSharePointReauthModalOpen(false)
          setSharePointReauthCustomViewId(null)
          loadCustomViews()
        }}
      />

      {/* Custom drag layer for smaller previews */}
      <CustomDragLayer />

      <BulkShareFilesModal
        open={bulkShareModalOpen}
        onClose={() => setBulkShareModalOpen(false)}
        fileIds={selectedFiles.map(f => f.id)}
        bp={bp}
      />

      <BulkEditFileModal
        open={bulkEditModalOpen}
        onClose={() => setBulkEditModalOpen(false)}
        fileIds={selectedFiles.map(f => f.id)}
        bp={bp}
      />

      <BulkUnshareFilesModal
        open={bulkUnshareModalOpen}
        onClose={() => setBulkUnshareModalOpen(false)}
        fileIds={selectedFileIds}
        bp={bp}
      />
    </Fragment>
  )
}

// Styled Components
const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  position: relative;

  /* Remove all selection borders and highlights in custom view */
  .file-card {
    outline: none !important;

    &:hover,
    &:focus,
    &:focus-within,
    &:active {
      outline: none !important;
    }
  }

  /* Remove selection highlights */
  [data-file-id] {
    &.selected,
    &[aria-selected='true'] {
      background-color: transparent !important;
      border: none !important;
      outline: none !important;
      box-shadow: none !important;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const FloatingMenuButton = styled.button`
  display: none;
  position: fixed;
  right: 16px;
  z-index: 900;

  @media (max-width: 768px) {
    ${floatingButtonStyle}
    display: inline-flex;
    color: #0c1229;
    position: fixed;
    right: 16px;
    z-index: 900;
    width: auto;
    gap: 6px;
    padding: 0 16px 0 14px;
    border-radius: 999px;

    svg {
      width: 20px;
      height: 20px;
      color: #1f8c97;
    }
  }
`

const FloatingMenuLabel = styled.span`
  font-size: 15px;
  font-weight: 700;
`

const MobileDrawerOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 20000;
        touch-action: none;
  }
`

const MobileDrawer = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 50vh;
    background: white;
    z-index: 20001;
    transform: translateY(${({ isOpen }) => (isOpen ? '0' : '-100%')});
    transition: transform 0.3s ease;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
    overflow: hidden;
    flex-direction: column;
  }
`

const MobileDrawerHeader = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #e0e0e0;
    flex-shrink: 0;
  }
`

const MobileDrawerTitle = styled.h3`
  display: none;
  @media (max-width: 768px) {
    display: block;
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #0c1229;
  }
`

const MobileDrawerClose = styled.button`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: #666;
    padding: 0;
    border-radius: 50%;
    transition: background 0.2s ease;

    &:hover {
      background: #f5f5f5;
    }

    svg {
      font-size: 20px;
    }
  }
`

const MobileDrawerContent = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    -webkit-overflow-scrolling: touch;
  }
`

const LeftPanel = styled.div`
  width: 300px;
  flex-shrink: 0;
  border-right: 1px solid #e0e0e0;
  height: 100%;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    display: none;
  }
`

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  max-width: 100%;
  overflow-x: hidden;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const SearchResultsIndicator = styled.div`
  padding: 10px 16px;
  margin-bottom: 16px;
  background: #f4faf4;
  border: 1px solid #d4ead4;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 8px;

  @keyframes cvResultsSlideIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
  animation: cvResultsSlideIn 0.25s ease-out;

  @media (max-width: 768px) {
    padding: 8px 12px;
    margin-bottom: 12px;
    font-size: 13px;
    flex-wrap: wrap;
    border-radius: 8px;
  }
`

const FileGridContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  overflow: visible;
  box-sizing: border-box;
  max-width: 100%;

  @media (max-width: 768px) {
    padding: 8px 4px;
    overflow-x: hidden;
  }
`

const BreadcrumbContainer = styled.div`
  margin-bottom: 16px;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
  isolation: isolate;

  @media (max-width: 768px) {
    margin-bottom: 12px;
    padding: 8px 4px;
  }
`

const BreadcrumbList = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`

const BreadcrumbItem = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ isActive, isClickable }) =>
    isActive ? '#0C1229' : isClickable ? '#33becc' : '#666'};
  font-weight: ${({ isActive }) => (isActive ? '600' : '500')};
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  pointer-events: ${({ isClickable }) => (isClickable ? 'auto' : 'none')};
  position: relative;
  z-index: 100;

  &:hover {
    background-color: ${({ isClickable }) =>
      isClickable ? '#f0f8ff' : 'transparent'};
  }
`

const BreadcrumbSeparator = styled.span`
  color: #999;
  font-size: 12px;
  display: flex;
  align-items: center;

  svg {
    font-size: 14px;
  }
`

const AddFolderButton = styled.button`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #33becc;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  background: transparent;
  border: 1px solid #33becc;

  &:hover {
    background-color: #f0f8ff;
    color: #2a9bb8;
    border-color: #2a9bb8;
  }
`

const ViewToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`

const StyledIconButton = styled(IconButton)`
  color: ${({ isActive, theme }) =>
    isActive ? theme.colors.secondary : 'inherit'} !important;
  padding: 8px !important;
  min-width: 36px !important;
  min-height: 36px !important;
  width: 36px !important;
  height: 36px !important;

  @media (max-width: 768px) {
    padding: 6px !important;
    min-width: 36px !important;
    min-height: 36px !important;
    width: 36px !important;
    height: 36px !important;
  }

  svg {
    @media (max-width: 768px) {
      font-size: 18px !important;
    }
  }

  &:hover {
    color: ${props => props.theme.colors.secondary};
  }
`

const ActionIconButton = styled(IconButton)`
  color: ${({ theme }) => theme.colors.secondary || '#33becc'};
  border: 1px solid ${({ theme }) => theme.colors.grey?.[400] || '#dbe5ef'};
  background-color: white;
  border-radius: 10px;
  padding: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    padding: 8px;
    min-width: 36px;
    min-height: 36px;
  }

  svg {
    @media (max-width: 768px) {
      font-size: 18px !important;
    }
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary || '#33becc'}08;
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(0);
  }
`

const SelectedFilesCount = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary || '#1b2b3c'};
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.colors.backgroundColor || '#f7f9fc'};
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.grey?.[400] || '#dbe5ef'};
  margin-right: 8px;
  display: flex;
  align-items: center;
  box-shadow: none;

  @media (max-width: 768px) {
    padding: 6px 10px;
    min-width: 36px;
    min-height: 36px;
    font-size: 13px;
    justify-content: center;
    box-shadow: none;
  }
`

const SelectedText = styled.span`
  /* Text is always visible on both desktop and mobile */
`

const SelectAllButton = styled.button`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary || '#33becc'};
  background: ${({ theme }) => theme.colors.backgroundColor || '#f7f9fc'};
  border: 1px solid ${({ theme }) => theme.colors.grey?.[400] || '#dbe5ef'};
  cursor: pointer;
  padding: 8px 12px;
  margin-right: 16px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 10px;
  box-shadow: none;
  min-width: 36px;
  min-height: 36px;
  height: 36px;

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 8px 12px;
    margin-right: 8px;
    min-height: 36px;
    min-width: 84px;
    width: auto;
    height: 36px;
    justify-content: center;
    box-shadow: none;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.secondary || '#33becc'};
    background-color: ${({ theme }) => theme.colors.secondary || '#33becc'}08;
    box-shadow: none;

    @media (max-width: 768px) {
      box-shadow: none;
    }
  }
`

const SelectAllText = styled.span``

const SortText = styled.span`
  @media (max-width: 768px) {
    display: none;
  }
`

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  max-width: 100%;

  @media (max-width: 768px) {
    overflow-x: hidden;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  padding: 8px 0;
`

const SkeletonCard = styled.div`
  padding: 8px;
`

const SearchLoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 16px;
  background: #f0fafb;
  border: 1px solid #d0eef2;
  border-radius: 10px;
  margin: 16px;

  @media (max-width: 768px) {
    margin: 8px;
    padding: 12px;
    border-radius: 8px;
  }
`

const SearchLoadingSpinner = styled(CircularProgress)`
  color: #33becc !important;
`

const SearchLoadingText = styled.div`
  color: #1a8a96;
  font-weight: 500;
  font-size: 13px;
`

const LoadingFilesContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: linear-gradient(
    135deg,
    rgba(51, 190, 204, 0.03) 0%,
    rgba(51, 190, 204, 0.08) 100%
  );
  border: 1px dashed rgba(51, 190, 204, 0.3);
  border-radius: 12px;
  margin: 24px;
  gap: 16px;
  min-height: 160px;
`

const LoadingFilesText = styled.div`
  color: #33becc;
  font-weight: 600;
  font-size: 15px;
  letter-spacing: 0.3px;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  text-align: center;
`

const FoldersSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
  box-sizing: border-box;
  max-width: 100%;
  width: 100%;

  @media (max-width: 768px) {
    display: none;
  }
`

const FolderCard = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;

  &:hover {
    border-color: #33becc;
    background: #f0f8ff;
  }

  @media (max-width: 768px) {
    padding: 10px 12px;
    border-radius: 6px;
  }
`

const FolderInfo = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`

const FolderName = styled.div`
  font-weight: 500;
  color: #0c1229;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`

const FolderCount = styled.div`
  font-size: 12px;
  color: #666;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

const FilesSection = styled.div`
  width: 100%;
  flex: 1;
  min-height: 400px;
  overflow: hidden;
  box-sizing: border-box;
  max-width: 100%;

  @media (max-width: 768px) {
    overflow-x: hidden;
    min-height: 300px;

    ${({ viewMode }) => viewMode === 'list' && `
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    `}
  }
`

const GridItemContainer = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
`

const ListScroller = styled.div`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
  padding-right: 4px;

  @media (max-width: 768px) {
    padding: 6px 12px 6px 6px;
  }
`

const CompactUploadZone = styled.div`
  display: ${({ canEdit }) => (canEdit ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  border: 2px dashed
    ${({ isActive }) => (isActive ? '#33becc' : 'rgba(51, 190, 204, 0.3)')};
  border-radius: 8px;
  background: ${({ isActive }) =>
    isActive ? 'rgba(51, 190, 204, 0.1)' : 'rgba(51, 190, 204, 0.05)'};
  color: ${({ isActive }) =>
    isActive ? '#33becc' : 'rgba(51, 190, 204, 0.8)'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  box-sizing: border-box;
  max-width: 100%;
  width: 100%;

  &:hover {
    border-color: #33becc;
    background: rgba(51, 190, 204, 0.1);
    color: #33becc;
  }

  @media (max-width: 768px) {
    padding: 10px 12px;
    margin-bottom: 12px;
    font-size: 13px;
    border-radius: 6px;
    flex-wrap: wrap;
    text-align: center;
  }

  ${({ isActive }) =>
    isActive &&
    `
    transform: scale(1.01);
    box-shadow: 0 2px 8px rgba(51, 190, 204, 0.2);
  `}

  input {
    display: none;
  }

  svg {
    flex-shrink: 0;
  }

  span {
    flex: 1;
    text-align: center;
  }
`

const UploadFilesCount = styled.div`
  padding: 4px 8px;
  background: rgba(51, 190, 204, 0.2);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #33becc;
`

const UploadDropZone = styled.div`
  display: ${({ canEdit }) => (canEdit ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  border: 3px dashed
    ${({ isActive }) => (isActive ? '#33becc' : 'rgba(51, 190, 204, 0.5)')};
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    ${({ isActive }) =>
        isActive ? 'rgba(51, 190, 204, 0.2)' : 'rgba(51, 190, 204, 0.08)'}
      0%,
    ${({ isActive }) =>
        isActive ? 'rgba(51, 190, 204, 0.12)' : 'rgba(51, 190, 204, 0.04)'}
      100%
  );
  color: ${({ isActive }) =>
    isActive ? '#33becc' : 'rgba(51, 190, 204, 0.8)'};
  font-weight: 500;
  min-height: 100px;
  margin-top: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    border-color: #33becc;
    color: #33becc;
    background: linear-gradient(
      135deg,
      rgba(51, 190, 204, 0.15) 0%,
      rgba(51, 190, 204, 0.08) 100%
    );
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(51, 190, 204, 0.2);
  }

  ${({ isActive }) =>
    isActive &&
    `
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(51, 190, 204, 0.3);
  `}

  input {
    display: none;
  }
`

const UploadFilesPreview = styled.div`
  margin-top: 12px;
  padding: 8px 16px;
  background: rgba(51, 190, 204, 0.1);
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  color: #33becc;
  border: 1px solid rgba(51, 190, 204, 0.3);
`

const FilePreviewContainer = styled.div`
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
  overflow: visible;

  @media (max-width: 768px) {
    height: auto;
    min-height: calc(100vh - 200px);
  }
`

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 12px 16px;
  }
`

const PreviewTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #0c1229;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  margin-right: 8px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`

const PreviewCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;

  &:hover {
    background: #e0e0e0;
    color: #333;
  }

  @media (max-width: 768px) {
    font-size: 20px;
    min-width: 36px;
    height: 36px;
    padding: 6px;
  }
`

const FilePreviewContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow: hidden;
  background: #fafafa;

  @media (max-width: 768px) {
    padding: 12px 8px;
    overflow-y: auto;
  }
`

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background: white;
`

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background: white;
`

const PreviewPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;

  /* Style the File component when used as fallback */
  > div {
    max-width: 400px;
    width: 100%;
  }
`

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${({ theme }) => theme.colors.secondary}10;
  border: 2px solid ${({ theme }) => theme.colors.secondary}30;
  padding: 4px 8px;
  margin-right: 10px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary}20;
    border-color: ${({ theme }) => theme.colors.secondary};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }

  svg {
    font-size: 18px;
  }
`

const SelectionControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
  padding: 4px 12px;
  background: #33becc15;
  border: 1px solid #33becc40;
  border-radius: 8px;
`

const SelectionCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #33becc;
`

const ClearSelectionButton = styled.button`
  background: transparent;
  border: 1px solid #33becc;
  color: #33becc;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #33becc;
    color: white;
  }
`

const SelectionOverlay = styled.div`
  position: absolute;
  border: 2px solid ${({ theme }) => theme.colors.secondary || '#33becc'};
  background-color: ${({ theme }) =>
    (theme.colors.secondary || '#33becc') + '25'};
  pointer-events: none;
  z-index: 1000;
  border-radius: 2px;
`

const DragSelectionHint = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 5px 9px;
  border: 1px solid ${({ theme }) => `${theme.colors.secondary || '#33becc'}40`};
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.9);
  color: ${({ theme }) => theme.colors.secondary || '#33becc'};
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(9, 21, 45, 0.08);
  backdrop-filter: blur(2px);
  pointer-events: none;
  user-select: none;
`

const DragPreviewContainer = styled.div`
  position: relative;
`

const FileDragHandle = styled.span`
  position: absolute;
  top: 6px;
  right: ${({ $listView }) => ($listView ? '78px' : '34px')};
  z-index: 25;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.9);
  color: #7b8799;
  cursor: grab;
  opacity: 0.55;
  transition: opacity 0.15s ease, color 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    color: #33becc;
    background: #fff;
    opacity: 1;
  }

  &:active {
    cursor: grabbing;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`

const MiniFileCard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  font-size: 11px;
  max-width: 180px;
  margin-bottom: 2px;
  position: relative;
`

const MiniThumbnail = styled.img`
  width: 20px;
  height: 20px;
  object-fit: cover;
  border-radius: 2px;
  flex-shrink: 0;
`

const MiniFileIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #666;

  svg {
    font-size: 18px;
  }
`

const MiniFileName = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`

const FileCountBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #33becc;
  color: white;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  border: 2px solid white;
  z-index: 20;
`
const ResumeUploadButton = styled.button`
  margin: 8px 0 16px 0;
  padding: 8px 14px;
  background: #33becc;
  color: white;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2aa3b0;
  }
`

const NoFoldersMessage = styled.div`
  padding: 16px;
  margin: 16px 0;
  background: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 8px;
  color: #856404;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`
const SelectionControlsSplit = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  padding: 4px 12px;
  background: #33becc15;
  border: 1px solid #33becc40;
  border-radius: 8px;

  .left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .right {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`

const SyncStatusBanner = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: rgba(51, 190, 204, 0.1);
  border-bottom: 1px solid rgba(51, 190, 204, 0.2);
  color: #33becc;
  font-size: 14px;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      background: rgba(51, 190, 204, 0.1);
    }
    50% {
      background: rgba(51, 190, 204, 0.15);
    }
    100% {
      background: rgba(51, 190, 204, 0.1);
    }
  }
`

const SortBottomSheetOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99999;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const SortBottomSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-radius: 20px 20px 0 0;
  z-index: 100000;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  padding-bottom: env(safe-area-inset-bottom);

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`

const SortBottomSheetHandle = styled.div`
  width: 40px;
  height: 4px;
  background: #d0d0d0;
  border-radius: 2px;
  margin: 12px auto 8px;
`

const SortBottomSheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
`

const SortBottomSheetTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  flex: 1;
`

const SortBottomSheetCloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 28px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f0f0;
  }
`

const SortBottomSheetContent = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 0;
`

const SortBottomSheetItem = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  background: ${({ selected }) => (selected ? '#f0f9ff' : 'transparent')};

  &:hover {
    background-color: ${({ selected }) => (selected ? '#f0f9ff' : '#f5f5f5')};
  }

  &:active {
    background-color: #e8e8e8;
  }
`

const SortBottomSheetText = styled.span`
  font-size: 16px;
  color: ${({ selected }) => (selected ? '#33becc' : '#1f2937')};
  font-weight: ${({ selected }) => (selected ? '600' : '400')};
`

export default CustomViews
